// Bangkok, "Loy Krathong on the river": the full-moon night of the twelfth lunar month from a wooden riverside terrace
// at Tha Tien, looking west across the Chao Phraya to Wat Arun on the Thonburi bank, floodlit gold: the tall central
// prang on its three steep terraces with the mondop and the stair at its foot, the four satellite prangs at the corners
// of its platform, the ordination hall's stacked roofs and the white Wichai Prasit fort downstream (left), the
// Chinese-style riverside pavilions and the ferry pier in front, shophouses, palms and a hotel upstream (right).
// Krathongs (banana-leaf floats with a candle, incense and flowers) drift downstream, right to left, in clusters with
// clumps of water hyacinth; a train of rice barges behind a tug, an orange-flag express boat, two long-tail boats with
// their spray tails and a lit dinner cruise pass by. On our side: a spirit house on its pillar with marigolds, candles
// and offerings, a frangipani tree, fairy lights on the railing, a noodle cart with steam and moths round its bulb,
// geckos on the lamp post, a soi dog asleep, and a couple at the water steps letting their krathong go.
// Frangipani flowers fall at three depths. A lit rooftop sign on a riverside shophouse right of the temple carries a
// mango sticky rice ad (or the ad-space pitch).
import { W, H, LOOP, TAU, frac, clamp, lerp, smooth, wave, step, hash, rng, bayer, hex,
  makeCanvas, px, rect, makeHalo, drawHalo, streak, reflect, FONT3, makePitch } from '../engine/core.js';
import { weatherPlans, drawWeather } from '../engine/weather.js';

let ctx;                                             // the scene's 480x270 context, set by create()
const halo = (hl, x, y, a = 1) => drawHalo(ctx, hl, x, y, a);

/* ================= Scene constants ================= */
const WL = 170;                                      // the Thonburi bank's waterline
const EDGE = 236;                                    // the front edge of our terrace deck
const RAIL = 218;                                    // the railing's top rail
const PX = 214;                                      // Wat Arun: the central prang's column
const PG = 158;                                      // the temple's ground line
const PLAT = 55;                                     // half-width of the prang's outer platform
const MOON = { x: 330, y: 46, r: 14 };
const BB = { x: 274, y: 110, w: 48, h: 20 };         // rooftop billboard face (exported as `billboard`)
const SHOP = { x0: 268, x1: 334, top: 140 };         // the riverside shophouse the sign stands on
const LANE = { far: 180, mid: 196, near: 214 };      // boat waterlines, far to near
const SH = { x: 46 };                                // the spirit house's centre column
const HALL = { x: 132 };                             // the Thai-roofed hall downstream of the prang
const CART = { x0: 378, x1: 452 };                   // the noodle cart
const BULB = { x: 414, y: 181 };                     // the bulb under its awning
const POT = { x: 434, y: 186 };                      // the broth pot (steam)
const POST = { x: 468, top: 142 };                   // the lamp post on the right, geckos on it
const LAMP = { x: 468, y: 146 };                     // its lamp
const GAP = { x0: 146, x1: 186 };                    // the gap in the railing where the water steps go down
const COUPLE = { w: 156, m: 168, y: 218 };           // the woman's and the man's sprite x, their top row
const DOG = { x: 316, y: 227 };

/* ================= Colour + pixel helpers ================= */
const toHex = (r, g, b) => '#' + ((1 << 24) | (r << 16) | (g << 8) | b).toString(16).slice(1);
const mixHex = (a, b, t) => { const A = hex(a), B = hex(b); return toHex(...A.map((v, i) => Math.round(lerp(v, B[i], t)))); };
const RGB = {};
const rgbOf = h => RGB[h] || (RGB[h] = hex(h));
/** ordered-dither pick from a palette (v in 0..1); soft < 1 narrows the dithered band between tones */
function dpick(pal, v, i, y, soft = 1) {
  const f = clamp(v, 0, 1) * (pal.length - 1);
  let k = Math.floor(f);
  if (f - k > 0.5 + (bayer(i, y) - 0.5) * soft) k++;
  return pal[Math.min(k, pal.length - 1)];
}
/** ImageData-backed painter for prerendering (hex colours). `own` tracks which object owns a pixel. */
function painter(w = W, h = H, track = false) {
  const [c, x] = makeCanvas(w, h);
  const img = x.createImageData(w, h), d = img.data;
  const own = track ? new Int32Array(w * h).fill(-1) : null;
  const p = { c, x, d, w, h, own, id: -1 };
  p.set = (i, y, col) => {
    i = Math.round(i); y = Math.round(y);
    if (i < 0 || y < 0 || i >= w || y >= h) return;
    const k = rgbOf(col), o = (y * w + i) * 4;
    d[o] = k[0]; d[o + 1] = k[1]; d[o + 2] = k[2]; d[o + 3] = 255;
    if (own) own[y * w + i] = p.id;
  };
  p.has = (i, y) => i >= 0 && y >= 0 && i < w && y < h && d[(y * w + i) * 4 + 3] > 0;
  p.done = () => { x.putImageData(img, 0, 0); return c; };
  return p;
}
/** sprite from rows of characters and a colour key ('.' or unknown = transparent) */
function sprite(rows, key, flip = false) {
  const h = rows.length, w = Math.max(...rows.map(r => r.length));
  const p = painter(w, h);
  rows.forEach((row, y) => { for (let i = 0; i < row.length; i++) { const col = key[row[i]]; if (col) p.set(flip ? w - 1 - i : i, y, col); } });
  return p.done();
}
function flipCanvas(src) {
  const [c, x] = makeCanvas(src.width, src.height);
  x.translate(src.width, 0); x.scale(-1, 1); x.drawImage(src, 0, 0);
  return c;
}
function paintRows(p, rows, key, x0, y0) {
  rows.forEach((row, j) => { for (let i = 0; i < row.length; i++) { const col = key[row[i]]; if (col) p.set(x0 + i, y0 + j, col); } });
}
/** FONT3 text straight into a painter, colour picked per glyph row */
function ptext(p, s, x, y, colAt) {
  let cx = x;
  for (const ch of s) {
    const g = FONT3[ch];
    if (g) g.forEach((row, j) => { for (let i = 0; i < 3; i++) if (row[i] === '1') p.set(cx + i, y + j, colAt(j)); });
    cx += 4;
  }
}
/** a straight 1-px line into a painter */
function pline(p, x0, y0, x1, y1, col) {
  const n = Math.max(Math.abs(x1 - x0), Math.abs(y1 - y0), 1);
  for (let k = 0; k <= n; k++) p.set(Math.round(lerp(x0, x1, k / n)), Math.round(lerp(y0, y1, k / n)), typeof col === 'function' ? col(k / n) : col);
}

/* ================= Sky: deep indigo overhead, a humid violet haze warmed by the temple toward the horizon ================= */
const SKY = ['#04041a', '#06061f', '#090925', '#0c0c2c', '#101033', '#15133a', '#1b1742', '#221b49', '#2a2050',
  '#332556', '#3d2b5a', '#48315d', '#54385e', '#613f5e', '#6e475c'].map(hex);
function renderSky() {
  const [c, x] = makeCanvas(W, H);
  const img = x.createImageData(W, H), d = img.data;
  for (let y = 0; y < H; y++) for (let i = 0; i < W; i++) {
    let v = Math.pow(Math.min(1, y / 166), 1.55);
    v += Math.max(0, 1 - Math.hypot((i - PX) / 200, (y - 150) / 96)) * 0.2;       // the floodlit temple in the haze
    v += Math.max(0, 1 - Math.hypot((i - 430) / 120, (y - 160) / 50)) * 0.06;     // the hotels upstream
    v -= Math.max(0, 1 - Math.hypot((i - MOON.x) / 150, (y - MOON.y) / 110)) * 0.05;   // cooler round the moon (its halo lights it)
    const f = clamp(v, 0, 1) * (SKY.length - 1);
    let k = Math.floor(f);
    if (f - k > bayer(i, y)) k++;
    const col = SKY[Math.min(k, SKY.length - 1)], o = (y * W + i) * 4;
    d[o] = col[0]; d[o + 1] = col[1]; d[o + 2] = col[2]; d[o + 3] = 255;
  }
  x.putImageData(img, 0, 0);
  return c;
}
const STARS = [];
{
  const r = rng(117);
  while (STARS.length < 26) {
    const x = Math.floor(r() * W), y = 4 + Math.floor(r() * 84);
    if (Math.hypot(x - MOON.x, y - MOON.y) < 40 || (x < 170 && y < 70)) continue;   // not in the moon's glare or the tree
    STARS.push({ x, y, k: 13 + Math.floor(r() * 60), ph: r(), b: 0.25 + r() * 0.45, big: r() < 0.08 });
  }
}
/** the full moon: creamy, a touch warm in the humid air, its maria making the hare that Thai tales see in it */
const MARIA = [[-0.3, -0.3, 0.32], [0.16, -0.38, 0.2], [0.3, -0.05, 0.24], [0.58, 0.14, 0.17], [-0.52, 0.08, 0.34],
  [-0.12, 0.47, 0.2], [0.7, -0.34, 0.12], [0.34, 0.44, 0.13]];
function renderMoon() {
  const R = MOON.r, s = 2 * R + 3, c0 = R + 1, p = painter(s, s);
  for (let y = 0; y < s; y++) for (let i = 0; i < s; i++) {
    const dx = (i - c0) / R, dy = (y - c0) / R, d = Math.hypot(i - c0, y - c0);
    if (d > R + 0.35) continue;
    let m = 0;
    for (const [mx, my, mr] of MARIA) m = Math.max(m, 1 - Math.hypot(dx - mx, dy - my) / mr);
    const v = clamp(m * 1.5, 0, 1) * 0.72 + Math.pow(d / R, 3) * 0.3;
    let col = dpick(['#fffbec', '#f7f0d2', '#e9dfba', '#d9cda4'], v, i, y, 0.7);
    if (d > R - 0.7) col = dx + dy > 0.4 ? '#e6d9b0' : '#fff6de';            // the limb
    p.set(i, y, col);
  }
  p.set(c0 - 1, c0 + Math.round(R * 0.72), '#ffffff');                       // Tycho's bright ray crater
  return p.done();
}

/* ================= Thin clouds (tileable streaky bands, moonlit tops, city-lit bellies) ================= */
const CLOUD_DEFS = [
  { seed: 31, y: 14, h: 40, banks: 2, k: 1, pal: { top: '#34305e', body: '#15143a', core: '#131236', bot: '#1c1a44' } },
  { seed: 47, y: 72, h: 40, banks: 3, k: 2, pal: { top: '#54487a', body: '#221c48', core: '#1f1a44', bot: '#3a2c52' } },
];
function makeCloudLayer(def) {
  const r = rng(def.seed), h = def.h, puffs = [];
  for (let b = 0; b < def.banks; b++) {
    const bx = r() * W, bw = 60 + r() * 100, base = h * (0.4 + r() * 0.4);
    const np = 5 + Math.floor(r() * 5);
    for (let i = 0; i < np; i++) {
      const rx = 9 + r() * 20, ry = 1.8 + r() * 2.8;
      puffs.push({ x: bx + (r() - 0.5) * bw, y: base - r() * ry * 1.6, rx, ry, base: base + ry * 0.2 });
    }
  }
  const D = new Float32Array(W * h);
  for (let y = 0; y < h; y++) for (let x = 0; x < W; x++) {
    let best = -9;
    for (const p of puffs) {
      let dx = x - p.x; dx -= Math.round(dx / W) * W;
      const dy = y - p.y;
      let v = 1 - (dx * dx) / (p.rx * p.rx) - (dy * dy) / (p.ry * p.ry);
      if (y > p.base) v -= (y - p.base) * 0.6;
      if (v > best) best = v;
    }
    best += 0.22 * Math.sin(TAU * 7 * x / W + y * 0.9 + def.seed) * Math.sin(TAU * 3 * x / W - y * 0.3);
    D[y * W + x] = best;
  }
  const get = (x, y) => (y < 0 || y >= h) ? -9 : D[y * W + ((x % W) + W) % W];
  const [c, cx] = makeCanvas(W, h);
  const img = cx.createImageData(W, h), d = img.data, P = {};
  for (const k in def.pal) P[k] = hex(def.pal[k]);
  for (let y = 0; y < h; y++) for (let x = 0; x < W; x++) {
    const th = (bayer(x, y) - 0.5) * 0.3, v = D[y * W + x];
    if (v + th <= 0.12) continue;
    let col;
    if (get(x, y - 1) + th <= 0.12) col = P.top;
    else if (get(x, y + 1) + th <= 0.12) col = P.bot;
    else if (v > 0.5 + th) col = P.core;
    else col = P.body;
    const o = (y * W + x) * 4;
    d[o] = col[0]; d[o + 1] = col[1]; d[o + 2] = col[2]; d[o + 3] = 255;
  }
  cx.putImageData(img, 0, 0);
  return { c, y: def.y, k: def.k };
}

/* ================= Distant Thonburi: hazy condo towers beyond the bank, red beacons on the tallest ================= */
const TOWERS = [[16, 10, 112], [30, 7, 126], [42, 12, 104], [58, 8, 128], [70, 9, 136], [92, 7, 132], [101, 10, 140],
  [300, 6, 132], [342, 9, 122], [354, 12, 108], [370, 7, 126], [384, 11, 100], [400, 7, 128], [412, 14, 114],
  [432, 8, 124], [446, 11, 106], [462, 9, 120], [474, 8, 132]];
function renderFarCity() {
  const p = painter(), r = rng(71), lights = [], beacons = [];
  for (const [x0, w, top] of TOWERS) {
    const col = mixHex('#2c2448', '#3a2c52', r()), edge = mixHex(col, '#6a5a86', 0.35);
    for (let y = top; y < 166; y++) for (let i = x0; i < x0 + w; i++) {
      let cc = col;
      if (y === top) cc = edge;
      else if (i === x0) cc = mixHex(col, '#5a4a74', 0.3);
      else if (i === x0 + w - 1) cc = mixHex(col, '#140f24', 0.35);
      p.set(i, y, cc);
    }
    if (w > 8) for (let i = x0 + 2; i < x0 + w - 2; i++) p.set(i, top - 1, col);       // a rooftop plant room
    for (let y = top + 3; y < 160; y += 3) for (let i = x0 + 1; i < x0 + w - 1; i += 2)
      if (r() < 0.22) lights.push({ id: lights.length, x: i, y, n: 1 + Math.floor(r() * 5), p: 0.55 + r() * 0.35, off: r(),
        col: r() < 0.75 ? '#e8b878' : '#b8c8f0' });
    if (top < 118) beacons.push({ x: x0 + (w >> 1), y: top - 2, ph: r() });
  }
  return { c: p.done(), lights, beacons };
}

/* ================= Wat Arun: a Khmer-style prang on three steep terraces, four satellite prangs, the mondop =================
   Every level is symmetric about an integer column; lighting only picks colours (floodlit from the front, a little from
   the left). Levels are listed from the bottom: terraces (balustrade, lit ledge, a wall of demons and monkeys holding the
   tier up), the redented body with its niche (Indra on the three-headed elephant; Phra Phai on his horse in the
   satellites), the corn-cob tiers with leaf-shaped antefixes at their corners, the lotus-bud knob and the gold spire
   with its trident. The central prang's body and corn-cob are one convex tower rising from a moulded plinth, its tiers
   many and fine; the satellites keep a few stepped tiers. Porcelain bits catch the light (glints, drawn per frame). */
const WA = ['#fff0c4', '#ffd98c', '#f6ba62', '#e09a48', '#bc7436', '#8e5028', '#5e3420', '#3c221a', '#261418'];
const PORC = ['#ffffff', '#dcefff', '#ffd6e2', '#d4f8e4', '#fff4b0'];
const GOLD = '#ffe27a', GOLD_D = '#c89032';
/** colour index of a floodlit round-ish surface at offset u (-1..1) across it */
const litIdx = u => Math.floor((1 - (0.25 + 0.75 * Math.max(0, 0.86 * Math.sqrt(Math.max(0, 1 - u * u)) - 0.3 * u))) * 6.5);
const CENTRAL = [
  { k: 'terrace', h: 11, hw: 33 }, { k: 'terrace', h: 11, hw: 27 }, { k: 'terrace', h: 11, hw: 21 },
  { k: 'plinth', ws: [18, 19, 20] }, { k: 'cob', h: 62, hw0: 17, hw1: 2, body: 14, niche: [7, 10], fig: 'erawan' },
  { k: 'knob', ws: [1, 2, 3, 3, 2, 2] }, { k: 'spire', h: 17 },
];
const SAT = [
  { k: 'terrace', h: 5, hw: 9 }, { k: 'terrace', h: 5, hw: 7 }, { k: 'body', h: 9, hw: 5, niche: [3, 5], fig: 'horse' },
  { k: 'tier', h: 5, hw0: 5, hw1: 4 }, { k: 'tier', h: 4, hw0: 4, hw1: 3 }, { k: 'tier', h: 4, hw0: 3, hw1: 3 },
  { k: 'tier', h: 3, hw0: 2, hw1: 2 }, { k: 'knob', ws: [1, 1, 2, 2] }, { k: 'spire', h: 9 },
];
const SAT_REAR = [
  { k: 'terrace', h: 4, hw: 8 }, { k: 'terrace', h: 4, hw: 6 }, { k: 'body', h: 8, hw: 4, niche: [3, 4] },
  { k: 'tier', h: 4, hw0: 4, hw1: 4 }, { k: 'tier', h: 4, hw0: 3, hw1: 3 }, { k: 'tier', h: 3, hw0: 3, hw1: 2 },
  { k: 'tier', h: 3, hw0: 2, hw1: 2 }, { k: 'knob', ws: [1, 1, 2] }, { k: 'spire', h: 8 },
];
const CENTRAL_BASE = 151, SAT_BASE = 151, REAR_BASE = 148;
const SATS = [-45, 45].map(o => PX + o), REARS = [-35, 35].map(o => PX + o);
const levelsHeight = L => L.reduce((s, l) => s + (l.ws ? l.ws.length : l.h), 0);
const TIP = CENTRAL_BASE - levelsHeight(CENTRAL) + 1;                 // the trident's top row
const SAT_TIP = SAT_BASE - levelsHeight(SAT) + 1;
const ERAWAN = ['..g..', '.gGg.', '.www.', 'wwWww', 'wW.Ww', 'w...w'];
const HORSE = ['.g.', 'wWw', 'w.w'];
function prang(p, cx, base, levels, shade, glints, r) {
  const set = (i, y, k) => p.set(i, y, WA[clamp(k + shade, 0, WA.length - 1)]);
  /** the body, rows y0..y1: pilasters (placed for half-width hw0) and the notched redented corners round the niche
      and its figure; hwAt(y) is the half-width per row, cornice a lit top row with a shadow under it */
  const body = (y0, y1, hw0, hwAt, cornice, [nw, nh], figName) => {
    for (let y = y0; y <= y1; y++) {
      const j = y - y0, hw = hwAt(y);
      for (let i = cx - hw; i <= cx + hw; i++) {
        const o = i - cx, a = Math.abs(o), u = o / (hw + 0.5);
        let k = litIdx(u);
        if (cornice && j === 0) k -= 1; else if (cornice && j === 1) k = 5; else if (y === y1) k = 6;
        else if (a === hw || a === hw0 - 3 || a === (hw0 >> 1)) k += 1;  // pilasters' shadowed flanks
        else if (a === hw0 - 1 || a === hw0 - 4) k -= 1;                // pilasters
        if (a === hw && j > 1 && (y - y0) % 3 === 0) k = 6;               // notches of the redented corners
        set(i, y, k);
        if (glints && j > 1 && y < y1 && r() < 0.08) glints.push({ x: i, y, id: glints.length });
      }
    }
    const ny = y1 - nh;                                                    // the niche: a pointed arch, dark inside
    for (let y = ny; y < y1; y++) {
      const hw = y === ny ? 0 : y === ny + 1 ? Math.max(0, (nw >> 1) - 1) : nw >> 1;
      for (let i = cx - hw; i <= cx + hw; i++) set(i, y, y === ny ? 3 : 7);
      if (y > ny + 1) { set(cx - hw - 1, y, 2); set(cx + hw + 1, y, 4); }  // its lit frame
    }
    const fig = figName === 'erawan' ? ERAWAN : figName === 'horse' ? HORSE : null;
    if (fig) paintRows(p, fig, { g: GOLD_D, G: GOLD, w: mixHex('#cfc2ae', WA[7], 0.2 + shade * 0.2), W: '#8e8070' }, cx - (fig[0].length >> 1), y1 - fig.length - 1);
  };
  let y1 = base;
  for (const L of levels) {
    const h = L.ws ? L.ws.length : L.h, y0 = y1 - h + 1;
    if (L.k === 'terrace') {
      for (let y = y0; y <= y1; y++) {
        const j = y - y0;
        for (let i = cx - L.hw; i <= cx + L.hw; i++) {
          const o = i - cx, a = Math.abs(o), u = o / (L.hw + 0.5);
          let k;
          if (j === 0) k = (o & 1) ? 5 : 1;                               // balustrade: a row of little posts
          else if (j === 1) k = litIdx(u) - 1;                            // the lit ledge
          else if (j === 2) k = 5;                                        // its shadow
          else if (y === y1) k = 6;                                       // footing
          else k = litIdx(u) + ((a % 3) === 1 ? 1 : 0) + (j === 3 ? -1 : 0);   // the figures holding the tier up
          if (a >= L.hw - 1 && j > 1) k += a === L.hw ? 2 : 1;            // the redented corners, in shade
          set(i, y, k);
          if (glints && j >= 3 && y < y1 && (a % 3) !== 1 && r() < 0.12) glints.push({ x: i, y, id: glints.length });
        }
      }
    } else if (L.k === 'body') {
      body(y0, y1, L.hw, () => L.hw, true, L.niche, L.fig);
    } else if (L.k === 'tier') {
      for (let y = y0; y <= y1; y++) {
        const j = y - y0, f = h > 1 ? (y1 - y) / (h - 1) : 0;
        const hw = Math.round(lerp(L.hw0, L.hw1, f)) + (j === 0 ? 1 : 0);
        for (let i = cx - hw; i <= cx + hw; i++) {
          const o = i - cx, u = o / (hw + 0.5);
          let k = litIdx(u);
          if (j === 0) k -= 1;                                            // the tier's lit rim
          else if (j === 1) k += 2;                                       // the shadow under it
          else if (Math.abs(o) === Math.round(hw * 0.55) || Math.abs(o) === hw) k += 1;   // the redented ridges, in shade
          else if (((o + y) & 1) === 0 && Math.abs(o) < hw) k += 1;       // porcelain mosaic
          set(i, y, k);
          if (glints && j > 1 && r() < 0.14) glints.push({ x: i, y, id: glints.length });
        }
        if (j === 0) { set(cx - hw, y - 1, 1); set(cx + hw, y - 1, 3); }   // antefixes at the corners, pointing up
        if (j === 3 && hw > 4) { set(cx, y, 5); set(cx, y + 1, 4); }       // a small niche on the front of each tier
      }
    } else if (L.k === 'plinth') {                                     // mouldings stepping out to the terrace
      L.ws.forEach((hw, j) => { for (let i = cx - hw; i <= cx + hw; i++) set(i, y0 + j, litIdx((i - cx) / (hw + 0.5)) + ((j & 1) ? 1 : -1)); });
    } else if (L.k === 'cob') {
      // the tower above the terraces: one smooth convex taper from hw0 at its foot to hw1 under the knob. Its foot is
      // the body with the niche; above it the corn-cob, banded by many fine tiers (thinner as it narrows), each a lit
      // rim, a soft shadow under it and a face of porcelain mosaic; leaf antefixes stand at every other rim's corners
      // and the redents run up it
      const hwAt = y => Math.round(L.hw1 + (L.hw0 - L.hw1) * (1 - ((y1 - y) / (h - 1)) ** 2.2));
      const top = y1 - L.body + 1;
      body(top, y1, L.hw0, hwAt, false, L.niche, L.fig);
      for (let b1 = top - 1, n = 0; b1 >= y0; n++) {
        const b0 = Math.max(y0, b1 - Math.round(2.2 + hwAt(b1) * 0.16) + 1);
        for (let y = b0; y <= b1; y++) {
          const j = y - b0, hw = hwAt(y);
          for (let i = cx - hw; i <= cx + hw; i++) {
            const o = i - cx, a = Math.abs(o);
            let k = litIdx(o / (hw + 0.5));
            if (j === 0) k -= 1;
            else if (j === 1) k += 1;
            else if (a === hw || a === Math.round(hw * 0.4) || a === Math.round(hw * 0.75)) k += o < 0 && a < hw ? -1 : 1;   // the redents
            else if (((o + y) & 1) === 0) k += 1;
            set(i, y, k);
            if (glints && j > 1 && r() < 0.14) glints.push({ x: i, y, id: glints.length });
          }
          if (j === 0 && (n & 1) && hw > 3) { set(cx - hw - 1, y, 1); set(cx + hw + 1, y, 4); }
        }
        b1 = b0 - 1;
      }
    } else if (L.k === 'knob') {
      L.ws.forEach((hw, j) => { for (let i = cx - hw; i <= cx + hw; i++) set(i, y0 + j, litIdx((i - cx) / (hw + 0.5)) - (j < 2 ? 1 : 0)); });
    } else if (L.k === 'spire') {
      for (let y = y0 + 2; y <= y1; y++) {
        p.set(cx, y, (y - y0) % 3 === 2 ? GOLD_D : GOLD);
        if (y > y0 + 5 && (y1 - y) % 4 === 1) { p.set(cx - 1, y, GOLD_D); p.set(cx + 1, y, GOLD_D); }   // rings
      }
      p.set(cx, y0, GOLD); p.set(cx - 1, y0 + 1, GOLD); p.set(cx, y0 + 1, '#fff4c0'); p.set(cx + 1, y0 + 1, GOLD_D);   // the trident
      p.set(cx - 1, y0 + 2, GOLD_D); p.set(cx + 1, y0 + 2, GOLD_D);
    }
    y1 = y0 - 1;
  }
}
function renderWatArun(p, glints) {
  const r = rng(909);
  p.id = -5;
  // the outer platform: balustrade, lit ledge, a wall of little arched niches
  for (let y = 152; y <= PG; y++) {
    const j = y - 152;
    for (let i = PX - PLAT; i <= PX + PLAT; i++) {
      const o = i - PX, a = Math.abs(o), u = o / (PLAT + 8);
      let k = litIdx(u);
      if (j === 0) k = (o & 1) ? 5 : 2; else if (j === 1) k -= 1; else if (j === 2) k = 5; else if (j === 6) k = 7;
      else if (j >= 3 && j <= 5 && (a % 6) === 3) k = j === 3 ? 5 : 7;
      if (a === PLAT && j > 0) k += 2;
      p.set(i, y, WA[clamp(k, 0, 8)]);
    }
  }
  for (const cx of REARS) prang(p, cx, REAR_BASE, SAT_REAR, 2, null, r);   // behind the tower, less in the floodlight
  prang(p, PX, CENTRAL_BASE, CENTRAL, 0, glints, r);
  // the steep stair up the front, through the balustrades, to the top terrace
  for (let y = CENTRAL_BASE; y >= CENTRAL_BASE - 32; y--) {
    for (let i = PX - 2; i <= PX + 2; i++) p.set(i, y, WA[(y & 1) ? (i < PX + 1 ? 1 : 2) : 6]);
    p.set(PX - 3, y, WA[4]); p.set(PX + 3, y, WA[5]);
  }
  for (const cx of SATS) prang(p, cx, SAT_BASE, SAT, 0, glints, r);
  // the east mondop at the foot: a porcelain porch with a doorway, a stacked pointed roof and a gold finial
  for (let y = 144; y <= 151; y++) for (let i = PX - 5; i <= PX + 5; i++) {
    const a = Math.abs(i - PX);
    let k = litIdx((i - PX) / 6);
    if (y === 144) k = 1; else if (y === 151) k = 6; else if (a <= 1 && y >= 146) k = y === 146 ? 3 : 8;
    else if (a === 5) k += 2;
    p.set(i, y, WA[clamp(k, 0, 8)]);
  }
  p.set(PX, 149, '#ffd060');                                                   // the Buddha image lit inside
  [7, 6, 5, 5, 4, 3, 2, 1].forEach((hw, j) => {
    const y = 143 - j;
    for (let i = PX - hw; i <= PX + hw; i++) p.set(i, y, WA[clamp(litIdx((i - PX) / (hw + 0.5)) + (j === 0 || j === 3 ? -1 : j === 1 || j === 4 ? 2 : 0), 0, 8)]);
  });
  for (let y = 128; y <= 135; y++) p.set(PX, y, (y & 1) ? GOLD : GOLD_D);
  p.set(PX - 7, 142, WA[1]); p.set(PX + 7, 142, WA[3]);                       // upturned eave tips
}

/* ================= The Thonburi bank: fort, ordination hall, trees, shophouses, palms, hotel, riverfront ================= */
const HROW = ['#2e2440', '#28203a', '#231c34', '#1e182e'];
const WINC = ['#ffd27a', '#ffc462', '#ffb050', '#f59a44', '#ffe2a0', '#d8e4ff'];
const TILE = ['#f09a58', '#d8703a', '#a84e2a'], TGREEN = '#3a8a5a', PED = '#a8382a';
function renderBank(glints) {
  const p = painter(W, H, true), r = rng(55), wins = [], lamps = [], flood = [];
  let bid = 0;
  const building = (bx, bw, top, bot, col0, winP, gap = 3) => {
    p.id = bid++;
    const col = mixHex(col0, r() < 0.5 ? '#3a2c4a' : '#2a2c48', r() * 0.35), roof = mixHex(col, '#7a6a98', 0.2);
    for (let y = top; y <= bot; y++) for (let i = bx; i < bx + bw; i++) p.set(i, y, col);
    for (let i = bx; i < bx + bw; i++) p.set(i, top, roof);
    for (let wy = top + 2; wy < bot - 1; wy += gap) for (let wx = bx + 1; wx < bx + bw - 1; wx += 2)
      if (r() < winP) wins.push({ id: wins.length, x: wx, y: wy, b: p.id, n: 1 + Math.floor(r() * 7), p: 0.4 + r() * 0.5, off: r(),
        col: WINC[Math.floor(r() * r() * WINC.length)] });
  };
  const tree = (cx, cy, rx, ry, lit = 0) => {
    p.id = -3;
    for (let y = Math.floor(cy - ry - 1); y <= Math.ceil(cy + ry); y++) for (let i = Math.floor(cx - rx - 1); i <= Math.ceil(cx + rx + 1); i++) {
      const dx = (i - cx) / rx, dy = (y - cy) / ry;
      const v = 1 - dx * dx - dy * dy + (bayer(i, y) - 0.5) * 0.45 + (r() - 0.5) * 0.3;
      if (v <= 0) continue;
      let col = v > 0.5 ? '#0e1a1e' : '#122026';
      if (dy < -0.2 && v < 0.55) col = dy < -0.6 ? '#3a4a3a' : '#22342e';        // moonlit crown
      if (lit && dx * lit > 0.2 && v < 0.6) col = '#4a4030';                     // warmed by the floodlights
      p.set(i, y, col);
    }
  };
  const palm = (bx, base, hgt, lean) => {
    p.id = -3;
    let tx = bx, ty = base - hgt;
    for (let y = base; y >= ty; y--) {
      const f = (base - y) / hgt, x = Math.round(bx + lean * f * f * hgt * 0.25);
      p.set(x, y, (y & 3) ? '#2a2230' : '#3a3040'); if (f < 0.3) p.set(x + 1, y, '#1e1826');
      tx = x;
    }
    for (let k = 0; k < 8; k++) {                                                // fronds, drooping
      const ang = -Math.PI / 2 + (k - 3.5) * 0.46, len = 8 + (k % 3);
      for (let s = 1; s <= len; s++) {
        const x = tx + Math.cos(ang) * s, y = ty + Math.sin(ang) * s * 0.7 + (s * s) / (len * 1.3);
        p.set(x, y, s < 3 ? '#1c2a22' : k < 4 ? '#24382c' : '#1a2820');
        if (s > 2 && s % 2 === 0) p.set(x, y + 1, '#16221c');
      }
    }
    p.set(tx, ty + 1, '#3a3020'); p.set(tx + 1, ty + 1, '#2e2618');             // coconuts
  };
  // the far row of low houses along the whole bank
  for (let bx = 0; bx < W;) {
    const bw = 5 + Math.floor(r() * 9);
    if (bx < PX - PLAT - 4 || bx > PX + PLAT + 2) building(bx, bw, 146 + Math.floor(r() * 8), 168, HROW[0], 0.3);
    bx += bw + (r() < 0.25 ? 2 : 0);
  }
  // trees behind the temple and round the ordination hall
  for (const [cx, cy, rx, ry, lit] of [[150, 140, 9, 7, 1], [110, 140, 11, 8, 0], [118, 148, 8, 6, 0], [158, 147, 7, 6, 1], [272, 142, 8, 7, -1],
    [262, 149, 7, 5, -1], [86, 146, 8, 5, 0], [340, 148, 9, 7, 0], [30, 150, 10, 8, 0], [60, 148, 8, 6, 0]]) tree(cx, cy, rx, ry, lit);
  // the Navy's old palace buildings behind the fort: white, colonial, a few lit windows
  building(66, 38, 146, 158, '#6a6078', 0.5, 4);
  p.id = -4;
  for (let i = 66; i < 104; i++) { p.set(i, 145, '#9a90a4'); p.set(i, 146, '#7a7088'); }
  for (let y = 141; y < 145; y++) { const a = 145 - y; for (let i = 85 - a * 2; i <= 85 + a * 2; i++) p.set(i, y, y === 141 || Math.abs(i - 85) >= a * 2 - 1 ? '#a89cb0' : '#7a7088'); }
  // Wichai Prasit fort at the river: a white crenellated bastion, the flagpole with its gaff
  p.id = -4;
  for (let i = 64; i <= 106; i++) {
    const bast = i >= 76 && i <= 94, top = bast ? 155 : 158;
    for (let y = top; y < WL; y++) {
      const u = (i - 64) / 42, j = y - top;
      let col = dpick(['#d4ccbc', '#b0a89c', '#8a8280', '#645c64'], 0.15 + u * 0.5 + (y > 165 ? 0.25 : 0) - (bast ? 0.1 : 0), i, y, 0.6);
      if (j === 0) col = ((i >> 1) & 1) ? '#e4dccc' : null;                       // merlons
      else if (j === 1) col = '#dcd4c4';
      else if (j === 2) col = '#6a6068';
      else if ((j === 5 || j === 6) && (i % 7) === 3) col = '#2a2228';             // embrasures
      if (col) p.set(i, y, col);
    }
  }
  for (let y = 124; y < 155; y++) p.set(86, y, y < 130 ? '#c8c2b8' : '#9a9290');   // flagpole
  pline(p, 86, 133, 90, 129, '#8a8480'); for (let i = 83; i <= 89; i++) p.set(i, 136, '#8a8480');
  flood.push({ x: 86, y: 160, rx: 26, ry: 12 });
  // a Thai-roofed hall among the trees downstream of the prang, gable end on: stacked roofs, gold bargeboards, chofa, naga hooks
  p.id = -5;
  { const c = HALL.x, g = 157;
    for (let y = 149; y <= g; y++) for (let i = c - 11; i <= c + 11; i++) {
      const a = Math.abs(i - c);
      let col = dpick(['#fff2da', '#e8d4b8', '#bca084', '#8a6a58'], 0.15 + (i - c + 11) / 32 + (y === 149 ? -0.2 : 0), i, y, 0.6);
      if ((a === 4 || a === 8) && y >= 151 && y <= 155) col = y === 151 ? GOLD : '#4a2418';   // windows
      if (a <= 1 && y >= 152) col = y === 152 ? GOLD : '#3a1a12';                  // door
      if ((a === 2 || a === 6 || a === 10) && y > 149) col = '#fff6e8';            // porch columns
      p.set(i, y, col);
    }
    const slope = (apexY, baseY, hw, y) => Math.round((y - apexY) / (baseY - apexY) * hw);
    // two shoulder roofs stepping down each side (orange tiles, green border, gold edge, naga hook at the end)
    for (const [y0, y1, a0, a1] of [[142, 149, 12, 17], [138, 144, 8, 13]]) for (const s of [-1, 1]) {
      for (let y = y0; y <= y1; y++) {
        const f = (y - y0) / (y1 - y0), a = Math.round(lerp(a0, a1, f));
        for (let k = 0; k < 3; k++) p.set(c + s * (a - k), y, k === 0 ? GOLD : k === 1 ? TILE[s < 0 ? 0 : 1] : TGREEN);
        p.set(c + s * (a - 3), y, TILE[2]);
      }
      p.set(c + s * (a1 + 1), y1 - 1, GOLD); p.set(c + s * (a1 + 1), y1 - 2, GOLD_D);
    }
    // the main gable: carved red-and-gold pediment framed by gold bargeboards, the chofa curling up at the apex
    for (let y = 129; y <= 148; y++) {
      const a = slope(127, 148, 10, y);
      for (let i = c - a; i <= c + a; i++) {
        const d = a - Math.abs(i - c);
        let col = d <= 1 ? (d === 0 ? GOLD : GOLD_D) : ((i + y) % 4 === 0 || (Math.abs(i - c) === 2 && y > 138)) ? '#e0a040' : PED;
        if (y === 148) col = GOLD_D;
        if (d === 2 && y > 132) col = TGREEN;
        p.set(i, y, col);
      }
    }
    for (let y = 123; y < 129; y++) p.set(c + (y < 125 ? 1 : 0), y, y < 125 ? '#fff0b0' : GOLD);   // chofa
    for (const s of [-1, 1]) { p.set(c + s * 11, 147, GOLD); p.set(c + s * 12, 146, GOLD); }           // hang hong
    flood.push({ x: c, y: 142, rx: 22, ry: 18 });
  }
  renderWatArun(p, glints);
  flood.push({ x: PX, y: 92, rx: 40, ry: 64 }, { x: SATS[0], y: 130, rx: 12, ry: 26 }, { x: SATS[1], y: 130, rx: 12, ry: 26 });
  // the shophouse the sign stands on (a roof terrace of its own; the sign's legs stand on it when it's shown)
  building(SHOP.x0, SHOP.x1 - SHOP.x0, SHOP.top, 164, '#4a4058', 0.55, 5);
  p.id = -6;
  for (let x = SHOP.x0 + 5; x < SHOP.x1 - 2; x += 11) for (let y = SHOP.top + 1; y < 160; y++) { p.set(x, y, '#6a6078'); p.set(x + 1, y, '#3a3248'); }   // pilasters
  for (const y of [146, 151, 156]) for (let i = SHOP.x0; i < SHOP.x1; i++) if (!((i - SHOP.x0 - 5) % 11 < 2)) p.set(i, y, '#3a3248');   // floor slabs
  for (let i = SHOP.x0 + 2; i < SHOP.x1 - 2; i += 4) p.set(i, SHOP.top - 3, ['#ffe6a0', '#ff9ab0', '#a0e8c0'][(i >> 2) % 3]);   // rooftop bar bulbs
  for (let i = SHOP.x0; i < SHOP.x1; i++) { p.set(i, SHOP.top - 1, '#6a6078'); p.set(i, SHOP.top, '#8a7e90'); }
  for (let i = SHOP.x0 + 1; i < SHOP.x1; i += 3) p.set(i, SHOP.top - 2, '#6a6078');   // parapet rail
  for (let y = 134; y < SHOP.top - 1; y++) for (let i = 322; i <= 329; i++) p.set(i, y, i < 325 ? '#6a6480' : '#4a4460');   // water tank
  for (let i = SHOP.x0 + 2; i < SHOP.x1 - 2; i++) { p.set(i, 160, '#ffcf80'); p.set(i, 161, '#e89a50'); }   // the lit riverside restaurant
  // palms, the hotel upstream, more houses
  palm(352, 160, 34, 1); palm(364, 161, 42, -1); palm(376, 160, 30, 1); palm(12, 158, 30, 1);
  building(384, 64, 124, 164, '#403858', 0.62, 3);
  p.id = -6;
  for (let i = 384; i < 448; i++) p.set(i, 123, '#7a7098');
  for (let x = 386; x < 448; x += 9) for (let y = 124; y < 164; y++) p.set(x, y, '#564c70');   // balcony piers
  for (let y = 116; y < 123; y++) for (let i = 404 + (123 - y); i < 428 - (123 - y); i++) p.set(i, y, y === 116 ? '#7a7098' : '#3a3252');   // the stepped crown
  for (let i = 406; i < 426; i += 3) p.set(i, 121, '#ffd890');
  building(452, 28, 140, 166, HROW[1], 0.5);
  // stilt houses on the water upstream, a lamp on each little landing
  for (let bx = 334; bx < W; bx += 16 + Math.floor(r() * 6)) {
    p.id = bid++;
    const bw = 10 + Math.floor(r() * 5), top = 155 + Math.floor(r() * 4);
    for (let y = top; y <= 166; y++) for (let i = bx; i < bx + bw; i++) p.set(i, y, y === top ? '#5a4a58' : (i - bx) % 4 === 0 ? '#2a2030' : '#3a2e40');
    for (let i = bx - 1; i <= bx + bw; i++) p.set(i, top - 1, '#6a5460');
    if (r() < 0.8) wins.push({ id: wins.length, x: bx + 3, y: top + 3, b: p.id, n: 2, p: 0.8, off: r(), col: '#ffc070' });
    for (let y = 167; y < WL; y++) { p.set(bx + 1, y, '#1e1826'); p.set(bx + bw - 2, y, '#1e1826'); }   // stilts
    lamps.push({ x: bx + bw - 1, y: top + 1, col: '#ffc070', dim: true });
  }
  // the temple's riverfront: courtyard, white balustraded river wall, lamps, Chinese-style pavilions, the ferry pier
  p.id = -7;
  for (let i = 110; i <= 266; i++) {
    for (let y = PG + 1; y <= 161; y++) p.set(i, y, dpick(['#8a6a58', '#6a5048', '#4a3638'], (y - PG) / 4 + Math.abs(i - PX) / 300, i, y));
    p.set(i, 162, (i & 1) ? '#f4ecde' : '#8a7a78');
    for (let y = 163; y < WL; y++) p.set(i, y, dpick(['#e8dccc', '#bca898', '#8a7470', '#4a3a40'], (y - 163) / 7 + 0.1, i, y, 0.6));
    if (i % 9 === 0) p.set(i, 165, '#6a5a5c');
  }
  for (let i = 267; i < 336; i++) for (let y = 162; y < WL; y++) p.set(i, y, y === 162 ? '#6a5048' : (i % 6 === 0 || y === 169) ? '#1a1420' : '#2e2230');   // the restaurant's deck
  for (const x of [120, 136, 152, 262]) {                                   // pavilions: posts, lit inside, curved roof
    for (let y = 157; y <= 161; y++) for (let i = x - 3; i <= x + 3; i++) p.set(i, y, Math.abs(i - x) === 3 ? '#e8e0d4' : y === 157 ? '#ffe0a0' : '#c89058');
    for (let i = x - 5; i <= x + 5; i++) p.set(i, 156, '#3e5a54');
    for (let i = x - 4; i <= x + 4; i++) p.set(i, 155, '#56766a');
    for (let i = x - 2; i <= x + 2; i++) p.set(i, 154, '#6a8a7a');
    p.set(x - 6, 155, '#56766a'); p.set(x + 6, 155, '#56766a'); p.set(x, 153, GOLD_D);
    lamps.push({ x, y: 158, col: '#ffd890' });
  }
  for (const x of [113, 128, 144, 164, 214, 226, 238, 250, 290, 310, 330]) {
    p.set(x, 161, '#3a3038'); p.set(x, 160, '#fff0c0'); lamps.push({ x, y: 160, col: '#ffe0a0' });
  }
  // the ferry pier: a pontoon under a little Thai-roofed shelter, a cool tube light
  for (let i = 172; i <= 208; i++) for (let y = 166; y < WL; y++) p.set(i, y, y === 166 ? '#8a8490' : y === 167 ? '#5a5260' : '#2a2430');
  for (const x of [176, 190, 204]) for (let y = 159; y < 166; y++) p.set(x, y, '#b8b0b0');
  for (let i = 173; i <= 207; i++) { p.set(i, 158, '#a8402a'); p.set(i, 159, '#6a2418'); }
  for (let k = 1; k <= 3; k++) for (let i = 190 - (6 - k * 2); i <= 190 + (6 - k * 2); i++) p.set(i, 158 - k, k === 3 ? GOLD : '#c04a2e');
  for (let i = 178; i <= 202; i++) p.set(i, 160, '#e8f4ff');
  lamps.push({ x: 190, y: 161, col: '#e0f0ff' });
  const vis = wins.filter(w => p.own[w.y * W + w.x] === w.b);                    // windows still showing on their own building
  vis.forEach((w, i) => { w.id = i; });
  return { c: p.done(), wins: vis, lamps, flood };
}

/* ================= Rooftop billboard: a steel sign on the shophouse, ringed with chasing bulbs ================= */
const BB_FR = { x: 268, y: 104, w: 60, h: 36 };      // the frame sprite's box (scene px): legs down to the roof (y 139)
const BB_BULBS = [];
for (let x = BB.x - 2; x <= BB.x + BB.w + 1; x += 3) BB_BULBS.push([x, BB.y - 2], [x, BB.y + BB.h + 1]);
for (let y = BB.y + 1; y < BB.y + BB.h; y += 3) BB_BULBS.push([BB.x - 2, y], [BB.x + BB.w + 1, y]);
BB_BULBS.forEach((b, k) => { b.push(k); });
const STEEL = ['#6a6a86', '#4a4a66', '#32324a', '#222236', '#16162a'];
function renderBillboardFrame() {
  const p = painter(BB_FR.w, BB_FR.h), S = (x, y, col) => p.set(x - BB_FR.x, y - BB_FR.y, col);
  const x0 = BB.x - 1, x1 = BB.x + BB.w, y0 = BB.y - 1, y1 = BB.y + BB.h, roof = SHOP.top - 2;
  for (let i = x0 - 2; i <= x1 + 2; i++) for (let y = y0 - 2; y <= y1 + 2; y++) {           // the bulb ring's backing
    const ring = i < x0 || i > x1 || y < y0 || y > y1;
    if (ring) S(i, y, (i === x0 - 2 || i === x1 + 2 || y === y0 - 2 || y === y1 + 2) ? STEEL[4] : STEEL[3]);
  }
  for (let i = x0; i <= x1; i++) { S(i, y0, STEEL[1]); S(i, y1, STEEL[2]); }
  for (let y = y0; y <= y1; y++) { S(x0, y, STEEL[1]); S(x1, y, STEEL[2]); }
  const LEGS = [x0 + 5, BB.x + (BB.w >> 1) - 1, x1 - 6];
  for (const lx of LEGS) for (let y = y1 + 3; y <= roof; y++) { S(lx, y, STEEL[3]); S(lx + 1, y, STEEL[2]); }
  for (let k = 0; k + 1 < LEGS.length; k++) {                                              // X bracing
    const a = LEGS[k] + 2, b = LEGS[k + 1] - 1;
    for (let i = a; i <= b; i++) { const f = (i - a) / (b - a); S(i, Math.round(lerp(y1 + 4, roof - 1, f)), STEEL[3]); S(i, Math.round(lerp(roof - 1, y1 + 4, f)), STEEL[3]); }
  }
  for (let i = x0 - 3; i <= x1 + 3; i++) S(i, y1 + 3, (i & 1) ? '#5a5670' : '#46425a');      // catwalk
  return p.done();
}
// the city's own ad: mango sticky rice on a banana leaf, MANGO / STICKY RICE, on a navy board
const AD_BG = ['#35649a', '#2a5588', '#214876', '#1a3c66', '#143156', '#10284a'];
const MSR_ROWS = [
  '...................',
  '.............og....',
  '..........ooOOg....',
  '........ooOYhhOo...',
  '...ccc.oOYYYhYYOo..',
  '..cCCCcoYYYYYYYYo..',
  '.wwcCcwoYYYYYYYYYo.',
  'wwWwwbwWoYYYYYYYYo.',
  'wWWwbwWWwoYYYYYYo..',
  'wwwwwwwwww.ooooo...',
  '.gGGGGGGGGGGGGGGg..',
  'ggGgGgGgGgGgGgGggg.',
  '..gggggggggggggg...',
];
const MSR_KEY = { o: '#c8641c', O: '#f08a24', Y: '#ffc83a', y: '#e49c26', h: '#fff2a8', c: '#fff4d6', C: '#ffffff',
  w: '#f6f1e8', W: '#d6cebf', b: '#e8b830', g: '#2e7a3a', G: '#5cb44c' };
const MSR = { x: 1, y: 3 };
const AD_LINES = [['STICKY', 'RICE'], ['OPEN', 'LATE']];
function renderAd(lines) {
  const p = painter(BB.w, BB.h);
  for (let y = 0; y < BB.h; y++) for (let i = 0; i < BB.w; i++) {
    const v = 0.15 + 0.7 * y / (BB.h - 1) + 0.15 * Math.abs(i - 30) / 30;
    p.set(i, y, dpick(AD_BG, v, i, y));
  }
  paintRows(p, MSR_ROWS, MSR_KEY, MSR.x, MSR.y);
  const mid = 34, row = (s, y, colAt) => ptext(p, s, mid - ((s.length * 4 - 1) >> 1), y, colAt);
  row('MANGO', 3, () => '#0a1830'); row('MANGO', 2, j => j < 2 ? '#fff0a0' : '#ffc83a');          // drop shadow, then the word
  lines.forEach((s, k) => row(s, 9 + k * 6, () => '#f6f1e6'));
  return p.done();
}
function drawOwnAd(t) {
  const q = t * 30 / LOOP + 0.3;                                                // the lower lines change every 8 s
  ctx.drawImage(AD_C[Math.floor(q) & 1], BB.x, BB.y);
  ctx.fillStyle = '#ffffff';                                                    // steam off the warm rice
  for (let k = 0; k < 3; k++) {
    const u = frac(t * (30 + k * 7) / LOOP + k * 0.37), y = BB.y + MSR.y + 3 - Math.floor(u * 5);
    const x = BB.x + MSR.x + 3 + k * 2 + Math.round(Math.sin(u * 6 + k * 2 + TAU * 11 * t / LOOP));
    ctx.globalAlpha = 0.7 * Math.sin(Math.PI * u); ctx.fillRect(x, y, 1, 1);
  }
  const g = t % 10;                                                             // a glint rolls over the mango
  if (g < 0.6) { ctx.globalAlpha = 0.9; ctx.fillRect(BB.x + MSR.x + 10 + Math.floor(g / 0.6 * 5), BB.y + MSR.y + 4 + Math.floor(g / 0.6 * 3), 1, 1); }
  ctx.globalAlpha = 1;
}
function drawBillboard(t, pitch) {
  halo(HL.bbSky, BB.x + (BB.w >> 1), BB.y + (BB.h >> 1), 0.8);
  ctx.drawImage(BB_FR_C, BB_FR.x, BB_FR.y);
  if (!PITCH.covers(t, pitch)) drawOwnAd(t);
  PITCH.draw(ctx, t, BB.x, BB.y, pitch);
  const s = step(t, 960);                                                       // chasing bulbs, 4 steps a second
  for (const [x, y, k] of BB_BULBS) {
    const on = (k + s) % 3 === 0;
    ctx.fillStyle = on ? '#fff4c0' : '#9a7a40'; ctx.fillRect(x, y, 1, 1);
    if (on && k % 2 === 0) halo(HL.bbBulb, x, y, 0.7);
  }
}

/* ================= Boats (all drawn facing right; flipped for the ones heading downstream) ================= */
const WOOD = ['#8a5a36', '#5a3a24', '#3a2418', '#1e1410'];
function makeLongtail() {
  const w = 68, h = 15, p = painter(w, h);
  for (let i = 20; i <= 60; i++) {                                              // hull: turquoise gunwale, dark varnish
    p.set(i, 10, i < 58 ? '#36b0a4' : '#2a8a80'); p.set(i, 11, WOOD[1]); p.set(i, 12, WOOD[2]); if (i > 21 && i < 59) p.set(i, 13, WOOD[3]);
  }
  for (let y = 10; y <= 12; y++) p.set(20, y, WOOD[0]);
  [[57, 9, 3], [59, 8, 3], [61, 7, 3], [63, 6, 2], [64, 5, 2], [65, 4, 1]].forEach(([x, y, n]) => {   // the upswept prow
    for (let k = 0; k < n; k++) { p.set(x + k, y, k === 0 ? '#36b0a4' : WOOD[1]); p.set(x + k, y + 1, WOOD[2]); }
  });
  for (let y = 10; y <= 12; y++) for (let i = 58; i <= 62 - (y - 10); i++) p.set(i, y, WOOD[2]);
  for (const [x, y, c] of [[65, 3, '#ff5a8a'], [66, 3, '#ffe04a'], [66, 4, '#5aff8a'], [67, 4, '#ff5a8a'], [66, 5, '#ffa030'], [67, 6, '#ffe04a']]) p.set(x, y, c);   // ribbons and a garland
  for (let i = 26; i <= 54; i++) { p.set(i, 3, '#d8c8a4'); p.set(i, 4, (i % 3 === 0) ? '#ffe6a0' : '#8a7a64'); }   // canopy with its bulbs
  for (const x of [27, 40, 53]) for (let y = 5; y <= 9; y++) p.set(x, y, '#9a8a78');
  for (const [x, shirt] of [[32, '#e04a5a'], [37, '#4a8ae0'], [45, '#f0d060'], [49, '#f4f0ea']]) {   // passengers
    p.set(x, 7, '#1a1418'); p.set(x + 1, 7, '#1a1418'); p.set(x, 8, '#c08a64'); p.set(x + 1, 8, '#a8704e');
    p.set(x, 9, shirt); p.set(x + 1, 9, shirt);
  }
  for (let y = 3; y <= 9; y++) p.set(22, y, y < 5 ? (y === 3 ? '#1a1418' : '#c08a64') : '#6a8a5a');   // the driver standing at the stern
  p.set(23, 6, '#c08a64'); pline(p, 23, 6, 19, 8, '#6a6a78');                  // hand on the tiller
  for (let y = 7; y <= 10; y++) for (let i = 12; i <= 19; i++) p.set(i, y, y === 7 ? '#e0e0ea' : i === 12 || y === 10 ? '#4a4a58' : (i + y) % 3 ? '#a8a8b8' : '#8a8a9a');   // the engine
  pline(p, 12, 10, 0, 14, '#9a9aa8');                                           // the long-tail shaft
  return p.done();
}
function makeExpress() {
  const w = 76, h = 17, p = painter(w, h), r = rng(313);
  for (let y = 11; y <= 16; y++) {
    const x1 = w - 1 - Math.round((y - 11) * 1.3);
    for (let i = y > 14 ? 2 : 0; i <= x1; i++) {
      let col = y === 11 ? '#f4eee2' : y === 12 ? '#e8782a' : y <= 14 ? '#d8d0c0' : y === 15 ? '#6a2a22' : '#3a1a18';
      if (y === 13 && i % 9 === 4 && i < x1 - 4) col = '#b8b0a4';
      p.set(i, y, col);
    }
  }
  for (let y = 5; y <= 10; y++) for (let i = 6; i <= 64; i++) {                // the open-sided cabin, cool tube light inside
    let col = (i - 6) % 5 === 0 ? '#e8e0d0' : y === 5 ? '#e8f8f0' : y < 8 ? '#c8e8d8' : '#9ac8b4';
    if ((i - 6) % 5 !== 0 && y >= 7 && hash(i, 7, 5) < 0.55) col = y === 7 ? '#1c1a20' : y === 8 ? '#b88a68' : ['#3a4a8a', '#8a2a3a', '#e0e0e0', '#3a6a3a'][Math.floor(hash(i, 3, 9) * 4)];
    p.set(i, y, col);
  }
  for (let i = 4; i <= 66; i++) { p.set(i, 4, '#f6f2ea'); p.set(i, 3, i < 8 || i > 62 ? '#c8c0b4' : '#e0d8cc'); }
  for (let y = 0; y <= 3; y++) for (let i = 57; i <= 67; i++) p.set(i, y, y === 0 ? '#f6f2ea' : (i > 58 && i < 66 && y < 3) ? '#26344e' : '#e0d8cc');   // wheelhouse
  p.set(62, 1, '#8affc0');
  for (let y = 0; y <= 3; y++) p.set(5, y, '#8a8a90');                         // the flagstaff at the stern (the flag flies per frame)
  r();
  return p.done();
}
function makeCruise() {
  const w = 96, h = 24, p = painter(w, h);
  for (let y = 17; y <= 23; y++) {
    const x1 = w - 1 - Math.round((y - 17) * 1.4);
    for (let i = y > 21 ? 3 : 1; i <= x1; i++) p.set(i, y, y === 17 ? '#3a3c5e' : y === 18 ? '#d0a850' : y === 23 ? '#0c0c1e' : '#1a1c3a');
  }
  for (let y = 10; y <= 16; y++) for (let i = 4; i <= 88; i++) {               // the dining deck: big warm windows
    const m = (i - 4) % 6;
    let col = y === 10 ? '#4a4264' : y === 16 ? '#2a2440' : m === 0 ? '#2a2440' : y < 12 ? '#ffe0a0' : '#ffc468';
    if (m !== 0 && y >= 13 && y <= 14 && hash(i, 11) < 0.35) col = '#3a2018';   // diners
    if (m === 3 && y === 15) col = '#fff4c0';                                     // candles on the tables
    p.set(i, y, col);
  }
  for (let i = 8; i <= 84; i++) { p.set(i, 9, '#e8e0d0'); if (i % 3 === 0) { p.set(i, 8, '#b8b0a8'); p.set(i, 7, '#b8b0a8'); } }   // top deck rail
  for (const x of [20, 34, 35, 52, 66, 67]) for (let y = 4; y <= 8; y++) p.set(x, y, y === 4 ? '#1a1418' : y === 5 ? '#b08060' : '#3a3050');   // people on deck
  for (let i = 10; i <= 80; i++) { p.set(i, 3, '#e8dcc0'); p.set(i, 2, '#b8aa90'); }   // the canopy
  for (let y = 0; y <= 3; y++) for (let i = 78 + y; i <= 86 - y; i++) p.set(i, y, y === 0 ? GOLD : '#d8a040');   // a little gilded gable
  return p.done();
}
function makeTug() {
  const w = 20, h = 12, p = painter(w, h);
  for (let y = 7; y <= 11; y++) for (let i = (y > 9 ? 2 : 0) + (y === 11 ? 1 : 0); i <= 19 - (y > 9 ? 1 : 0); i++) p.set(i, y, y === 7 ? '#8a3a2a' : y === 11 ? '#0a0a12' : '#161620');
  p.set(0, 8, '#2a2a30'); p.set(0, 9, '#2a2a30');                              // tyre fender on the bow
  for (let y = 2; y <= 6; y++) for (let i = 5; i <= 12; i++) p.set(i, y, y === 2 ? '#e0dcd8' : (y === 3 || y === 4) && i > 5 && i < 12 && i % 2 ? '#ffd890' : '#b8b4b0');
  for (let y = 1; y <= 6; y++) { p.set(14, y, '#2a2a30'); p.set(15, y, y < 3 ? '#c83a2a' : '#1e1e26'); }   // stack
  p.set(8, 0, '#8a8a90'); p.set(8, 1, '#8a8a90');
  for (let i = 17; i <= 19; i++) p.set(i, 6, '#4a4a52');                       // the towing bitt
  return p.done();
}
function makeBarge(k) {
  const w = 32, h = 9, p = painter(w, h);
  for (let y = 5; y <= 8; y++) for (let i = y === 8 ? 1 : 0; i <= (y === 8 ? 30 : 31); i++) p.set(i, y, y === 5 ? '#6a4a30' : y === 8 ? '#120c0a' : '#2e1c14');
  for (let y = 1; y <= 4; y++) for (let i = 3; i <= 23; i++) p.set(i, y, y === 1 ? (i > 4 && i < 22 ? '#7a6048' : null) || '#4a3424' : (i - 3) % 4 === 0 ? '#22160e' : y === 2 ? '#5a4230' : '#3e2a1c');
  for (let y = 0; y <= 4; y++) for (let i = 25; i <= 30; i++) p.set(i, y, y === 0 ? '#5a4a3a' : (i === 27 || i === 28) && (y === 2 || y === 3) ? (k === 1 ? '#3a2a20' : '#ffc070') : '#3a281c');
  return p.done();
}
function makeFerry() {
  const w = 26, h = 9, p = painter(w, h);
  for (let y = 5; y <= 8; y++) for (let i = y === 8 ? 2 : 0; i <= 25 - (y > 6 ? y - 6 : 0); i++) p.set(i, y, y === 5 ? '#f0ece4' : y === 6 ? '#2a4a8a' : y === 8 ? '#1a1a2a' : '#d0ccc4');
  for (let i = 2; i <= 23; i++) p.set(i, 0, '#c8c0b8');
  for (let y = 1; y <= 4; y++) for (let i = 2; i <= 23; i++) p.set(i, y, (i - 2) % 5 === 0 ? '#e0dcd4' : y === 1 ? '#f0fff4' : (y >= 3 && hash(i, 2) < 0.4) ? '#2a2228' : '#c8e4d4');
  return p.done();
}

/* ================= Krathongs and water hyacinth ================= */
const KFLOW = [['#ffa21e', '#ffd040'], ['#ff5a96', '#ffb0d0'], ['#fff0e0', '#e0d0bc'], ['#b060e0', '#dca8ff']];
const KR_ROWS = [
  ['.f.', 'lll'],
  ['..c..', '.fFf.', 'lLlLl', '.bbb.'],
  ['..i.c.i..', '..i.c.i..', '...fFf...', '.lfFfFfl.', 'lLlLlLlLl', '.bbbbbbb.', '..ddddd..'],
];
const KR_CANDLE = [[1, -1], [2, -1], [4, -1]];                                // where each size's flame sits (x, y) in its sprite
function makeKrathong(size, fl) {
  return sprite(KR_ROWS[size], { f: fl[0], F: fl[1], l: '#2e7a34', L: '#62b84c', b: '#d8d09c', d: '#4a4a36', c: '#fff4e0', i: '#7a4a2e' });
}
const HY_ROWS = [
  ['.hp.', 'hhhh'],
  ['..p.P..', 'hHhhHhh', '.ddddd.'],
  ['...p..P.....', '.hHhhHhh.h..', 'hhHhhhhHhhhh', '.dddddddddd.'],
];
const makeHyacinth = size => sprite(HY_ROWS[size], { h: '#1e3a26', H: '#40703e', p: '#a888d8', P: '#e0ccff', d: '#0c1812' });

/* ================= Our terrace: deck, railing, water steps, frangipani, spirit house, noodle cart, lamp post ================= */
const RAIL_POSTS = [40, 72, 104, GAP.x0 - 2, GAP.x1, 220, 252, 284, 316, 348, 380, 412, 444, 476];
const TEAK = ['#1a0e0a', '#2e1c12', '#4a2e1c', '#6e4428', '#9a6034', '#c48448'];
// warm light pools on the terrace: the cart's bulb, the spirit house's candles, the lamp post, the fairy lights
const POOLS = [[BULB.x, 196, 96, 1], [SH.x, 196, 64, 0.8], [LAMP.x, 170, 90, 0.8], [170, 226, 70, 0.5], [270, 226, 90, 0.4]];
const poolAt = (i, y) => { let v = 0; for (const [x0, y0, r, a] of POOLS) v = Math.max(v, a * Math.max(0, 1 - Math.hypot(i - x0, (y - y0) * 1.3) / r)); return v; };
// the tree: branch segments [x0, y0, x1, y1, w0, w1] and the leaf rosettes (with their flower clusters) at the tips
const BRANCHES = [[7, 250, 16, 122, 10, 7], [16, 122, 10, 70, 6, 5], [10, 70, 4, 30, 5, 4], [10, 70, 30, 40, 4, 3],
  [16, 122, 44, 92, 6, 5], [44, 92, 58, 56, 5, 4], [58, 56, 52, 26, 4, 3], [58, 56, 84, 36, 4, 3], [44, 92, 90, 74, 5, 4],
  [90, 74, 118, 52, 4, 3], [90, 74, 130, 80, 4, 3], [118, 52, 146, 44, 3, 3], [130, 80, 156, 72, 3, 3]];
const ROSETTES = [[4, 28, 10], [30, 38, 9], [52, 24, 10], [84, 34, 10], [118, 50, 9], [146, 42, 9], [156, 70, 8], [10, 70, 7], [96, 70, 7]];
const TREE_WIRE = [[0, 100], [26, 98], [58, 90], [92, 82], [124, 86], [158, 76]];
function renderForeground() {
  const p = painter(W, H, true), r = rng(88), railBulbs = [], treeBulbs = [];
  p.id = 0;
  // the deck: an edge beam, then planks widening toward us, warm where the lights fall
  for (let y = EDGE; y < H; y++) for (let i = 0; i < W; i++) {
    const L = poolAt(i, y);
    let v = 0.25 + L * 0.7, col;
    if (y === EDGE) col = TEAK[clamp(Math.round(2 + L * 3.4), 0, 5)];
    else if (y === EDGE + 1) col = TEAK[clamp(Math.round(1 + L * 2.6), 0, 5)];
    else if (y === EDGE + 2) col = TEAK[0];
    else {
      const seams = [239, 244, 250, 257, 265], si = seams.filter(s => s <= y).length - 1, top = seams[si], nxt = seams[si + 1] ?? H;
      if (y === nxt - 1) v -= 0.3; else if (y === top) v += 0.12;
      if (Math.floor(hash(si, Math.floor((i + si * 37) / (40 + si * 9)), 3) * 6) === 0 && (i + si * 37) % (40 + si * 9) === 0) v -= 0.35;
      v -= (y - EDGE) / 110;
      col = dpick(TEAK.slice(0, 5), v, i, y, 0.8);
    }
    p.set(i, y, col);
  }
  // the water steps in the gap: two wet stone treads going down to the river
  for (let i = GAP.x0; i < GAP.x1; i++) {
    for (let y = 231; y < EDGE; y++) p.set(i, y, y === 231 || y === 234 ? '#8a7a74' : y === 232 ? '#3a3038' : '#5a4c50');
    p.set(i, EDGE, '#6a5a58'); p.set(i, EDGE + 1, '#3a3036');
  }
  // the railing: teak posts, a top rail and a middle rail (none across the steps)
  const wood = (i, y, base) => TEAK[clamp(Math.round(base + poolAt(i, y) * 2.4), 0, 5)];
  for (let i = 0; i < W; i++) {
    if (i >= GAP.x0 - 2 && i < GAP.x1 + 3) continue;
    p.set(i, RAIL, wood(i, RAIL, 3)); p.set(i, RAIL + 1, wood(i, RAIL, 2)); p.set(i, RAIL + 2, TEAK[1]);
    p.set(i, 228, wood(i, 228, 2)); p.set(i, 229, TEAK[0]);
  }
  for (const x of RAIL_POSTS) for (let y = RAIL - 1; y < EDGE; y++) {
    p.set(x, y, wood(x, y, 3)); p.set(x + 1, y, wood(x, y, 2)); p.set(x + 2, y, TEAK[1]);
    if (y === RAIL - 1) { p.set(x, y, wood(x, y, 4)); p.set(x + 1, y, wood(x, y, 3)); }
  }
  // fairy lights along the railing: a wire sagging from post to post (bulbs are lit per frame)
  for (let k = 0; k + 1 < RAIL_POSTS.length; k++) {
    const a = RAIL_POSTS[k] + 2, b = RAIL_POSTS[k + 1];
    if (a > GAP.x0 - 3 && b < GAP.x1 + 1) continue;
    for (let i = a; i < b; i++) {
      const f = (i - a) / (b - a), y = Math.round(RAIL + 3 + 3.2 * Math.sin(Math.PI * f));
      p.set(i, y, '#2a1e18');
      if ((i - a) % 4 === 2) railBulbs.push({ x: i, y: y + 1 });
    }
  }
  // the frangipani: smooth grey forking branches, leaf rosettes at the tips, flowers in their hearts
  p.id = 1;
  for (const [x0, y0, x1, y1, w0, w1] of BRANCHES) {
    const n = Math.ceil(Math.hypot(x1 - x0, y1 - y0));
    for (let s = 0; s <= n; s++) {
      const f = s / n, cx = lerp(x0, x1, f), cy = lerp(y0, y1, f), w = lerp(w0, w1, f);
      for (let k = -w / 2; k <= w / 2; k += 0.5) {
        const x = Math.round(cx + k), y = Math.round(cy), u = (k + w / 2) / w;
        p.set(x, y, u < 0.25 ? '#8a8078' : u < 0.6 ? '#5e5650' : '#3a3434');
      }
    }
  }
  for (let y = 170; y < 250; y += 7 + Math.floor(r() * 5)) p.set(Math.round(lerp(7, 16, (250 - y) / 128)) + 1, y, '#2a2626');   // leaf scars on the trunk
  const LEAF = ['#10261a', '#1a3a24', '#26502e', '#3c6a38', '#5a7a3a'];
  for (const [tx, ty, n] of ROSETTES) {
    // broad oblong leaves in a fan from the branch tip, drooping at their ends; solid blades with a pale midrib
    for (let k = 0; k < n; k++) {
      const ang = -Math.PI / 2 + (k / (n - 1) - 0.5) * 3.7 + (r() - 0.5) * 0.3, len = 11 + r() * 5, wmax = 2.2 + r() * 0.8, sag = 0.22 + r() * 0.2;
      const ca = Math.cos(ang), sa = Math.sin(ang);
      for (let y = Math.floor(ty - len - 3); y <= Math.ceil(ty + len + 8); y++) for (let x = Math.floor(tx - len - 3); x <= Math.ceil(tx + len + 3); x++) {
        const dx = x + 0.5 - tx, dy0 = y + 0.5 - ty, s0 = dx * ca + dy0 * sa;
        if (s0 < 1 || s0 > len + 2) continue;
        const dy = dy0 - sag * s0 * s0 / len, s = dx * ca + dy * sa, d = -dx * sa + dy * ca;
        if (s < 1.5 || s > len) continue;
        const hw = wmax * Math.pow(Math.sin(Math.PI * Math.min(1, s / len)), 0.6);
        if (Math.abs(d) > hw) continue;
        const lit = poolAt(x, y) > 0.12 ? 1 : 0;
        let q = Math.abs(d) < 0.5 ? 3 : d < 0 ? 2 : 1;
        if (Math.abs(d) > hw - 0.7) q = 0;
        if (s > len * 0.8 && q > 0) q -= 1;
        p.set(x, y, LEAF[Math.min(4, q + lit)]);
      }
    }
    for (let k = 0; k < 5; k++) {                                               // a cluster of flowers in the rosette's heart
      const fx = tx - 4 + Math.round(r() * 8), fy = ty - 4 + Math.round(r() * 5), pink = r() < 0.25;
      paintRows(p, FLOWER_ROWS[k < 2 ? 2 : 1], pink ? FLOWER_KEYS[1] : FLOWER_KEYS[0], fx - 2, fy - 2);
    }
  }
  for (let k = 0; k + 1 < TREE_WIRE.length; k++) {                            // fairy lights draped through the branches
    const [ax, ay] = TREE_WIRE[k], [bx, by] = TREE_WIRE[k + 1];
    for (let i = ax; i < bx; i++) {
      const f = (i - ax) / (bx - ax), y = Math.round(lerp(ay, by, f) + 4 * Math.sin(Math.PI * f));
      p.set(i, y, '#1e1a16');
      if ((i - ax) % 5 === 3) treeBulbs.push({ x: i, y: y + 1 });
    }
  }
  // the spirit house on its pillar: red and gold, stacked roofs, a gilded spire, offerings on its tray
  const S = SH.x, SGOLD = ['#fff0a0', '#f0c050', '#c08a30', '#7a5220'], SRED = ['#e85a48', '#c83030', '#8a1e22', '#5a1218'];
  for (let y = 197; y < 250; y++) for (let i = S - 3; i <= S + 3; i++) {
    const u = (i - S + 3) / 6;
    let col = dpick(['#fff6e6', '#e8dcc4', '#b8a890', '#7a6a5a'], 0.1 + u * 0.8 + (y > 236 ? 0.2 : 0), i, y, 0.5);
    if (y === 200 || y === 201 || y === 214 || y === 215) col = u < 0.5 ? SGOLD[1] : SGOLD[2];
    p.set(i, y, col);
  }
  for (let y = 193; y <= 196; y++) for (let i = S - 18; i <= S + 18; i++) p.set(i, y, y === 193 ? SGOLD[0] : y === 196 ? SGOLD[2] : i < S ? SRED[1] : SRED[2]);
  for (const x of [S - 18, S + 18]) { p.set(x, 192, SGOLD[1]); p.set(x, 191, SGOLD[0]); }
  for (let y = 177; y <= 192; y++) for (let i = S - 9; i <= S + 9; i++) {
    const a = Math.abs(i - S);
    let col = i < S - 3 ? SRED[1] : i > S + 5 ? SRED[2] : SRED[1];
    if (a === 8 || a === 4) col = i < S ? SGOLD[1] : SGOLD[2];                  // gilded columns
    if (y === 177 || y === 178) col = SGOLD[y === 177 ? 0 : 2];
    if (a <= 2 && y >= 182) col = y === 182 ? SGOLD[0] : '#ffd890';              // the doorway, lit inside
    if (a === 6 && y >= 182 && y <= 186) col = '#ffc870';                        // little windows
    p.set(i, y, col);
  }
  for (let y = 184; y <= 188; y++) p.set(S, y, y === 184 ? '#fff4c0' : '#e8b040');   // the guardian spirit's figure in the door
  const tier = (y0, y1, a0, a1, tipUp) => {
    for (let y = y0; y <= y1; y++) {
      const a = Math.round(lerp(a0, a1, (y - y0) / Math.max(1, y1 - y0)));
      for (let i = S - a; i <= S + a; i++) {
        const d = a - Math.abs(i - S), u = (i - S + a) / (2 * a + 1);
        let col = dpick(SRED.slice(0, 3), 0.1 + u * 0.85, i, y, 0.5);
        if ((y1 - y) % 2 === 1) col = mixHex(col, SRED[3], 0.35);                // rows of tiles
        p.set(i, y, d === 0 ? SGOLD[1] : y === y1 ? SGOLD[2] : col);
      }
    }
    if (tipUp) for (const s of [-1, 1]) { p.set(S + s * (a1 + 1), y1 - 1, SGOLD[0]); p.set(S + s * (a1 + 2), y1 - 2, SGOLD[1]); }
  };
  tier(170, 176, 9, 13, true); tier(162, 169, 6, 10, true); tier(155, 161, 3, 7, true);
  [[154, 3], [153, 2], [152, 3], [151, 2], [150, 2], [149, 1], [148, 2], [147, 1], [146, 1], [145, 1], [144, 0], [143, 0], [142, 0]].forEach(([y, a]) => {
    for (let i = S - a; i <= S + a; i++) p.set(i, y, i < S ? SGOLD[0] : i === S ? SGOLD[1] : SGOLD[2]);
  });
  for (let i = S - 9; i <= S + 9; i++) { const y = 179 + Math.round(2 * Math.sin(Math.PI * (i - S + 9) / 18)); p.set(i, y, (i & 1) ? '#ff9a1e' : '#ffc83a'); }   // marigold swag
  for (const x of [S - 17, S + 17]) for (let y = 197; y < 208; y++) p.set(x + ((y >> 1) & 1), y, y % 3 ? '#ffa21e' : '#fff4e0');   // hanging garlands
  for (let y = 202; y <= 205; y++) for (let i = S - 4; i <= S + 4; i++) if ((i + y) & 1) p.set(i, y, y === 205 ? '#fff4e0' : '#ff9a1e');   // garland round the pillar
  paintRows(p, ['.w..', 'www.', 'w.w.'], { w: '#f4f0e8' }, S - 16, 190);        // a white elephant figurine
  paintRows(p, ['g', 'r', 'r', 'g'], { g: SGOLD[0], r: '#d83a5a' }, S - 12, 189);   // a dancer figurine
  paintRows(p, ['.o.g.', 'ooggo', 'wwwww'], { o: '#ff9a2a', g: '#6ab04a', w: '#e8e4dc' }, S - 8, 190);   // a plate of fruit
  paintRows(p, ['.iii.', 'ppppp', 'ppppp'], { i: '#7a4a2e', p: '#8a6a4a' }, S + 5, 190);   // the incense pot (tips glow per frame)
  for (const x of [S + 11, S + 13, S + 15]) { p.set(x, 188, '#e8e8e0'); for (let y = 189; y <= 192; y++) p.set(x, y, y === 189 ? '#ff6a5a' : '#d82a2a'); }   // red soda with straws
  for (const x of [S - 3, S + 3]) for (let y = 190; y <= 192; y++) p.set(x, y, '#fff4e0');   // candles (flames per frame)
  // the noodle cart: striped awning, glass case with noodles and fish balls, the broth pot, painted cabinet, bicycle wheels
  const C0 = CART.x0, C1 = CART.x1;
  for (let y = 166; y <= 171; y++) for (let i = C0 - 4; i <= C1 + 4; i++) {
    const red = ((i - C0 + 40) >> 2) & 1;
    p.set(i, y, y === 166 ? (red ? '#e85a50' : '#fff4dc') : y === 171 ? (red ? '#8a1e22' : '#b8a890') : red ? '#c8302e' : '#efe2c4');
  }
  for (let i = C0 - 4; i <= C1 + 4; i++) { const k = (i - C0 + 4) % 5, d = k === 0 || k === 4 ? 1 : 3; for (let y = 172; y < 172 + d; y++) p.set(i, y, y === 171 + d ? '#efe2c4' : '#b0282a'); }
  for (const x of [C0, C1]) for (let y = 175; y < 200; y++) { p.set(x, y, '#b8b8c4'); p.set(x + 1, y, '#6a6a78'); }
  for (let y = 175; y < BULB.y - 1; y++) p.set(BULB.x, y, '#2a2a30');
  for (const [x, len] of [[424, 5], [427, 7], [430, 4]]) for (let y = 175; y < 175 + len; y++) p.set(x, y, y === 174 + len ? '#d8d8e0' : '#8a8a98');   // hanging ladles
  for (let y = 184; y <= 199; y++) for (let i = 382; i <= 418; i++) {
    const frame = y === 184 || y === 199 || i === 382 || i === 400 || i === 418;
    let col = frame ? (y === 184 ? '#e8e8f0' : '#9a9aa8') : y < 188 ? '#5a7a88' : '#3a5462';
    if (!frame && y >= 192 && i < 398) col = ((i * 3 + y * 5) % 7 < 3) ? '#fff0a0' : '#e8c858';   // noodles
    if (!frame && y >= 194 && i > 402 && i < 417) col = ((i + y) & 1) ? '#f4ece0' : '#d8ccb8';     // fish balls
    if (!frame && y >= 189 && y < 193 && i > 402 && i < 417) col = (i % 3) ? '#6ab04a' : '#e8f0d0';   // greens and bean sprouts
    if (!frame && (y === 186 || y === 187) && ((i - 382) % 9 === 3)) col = '#c8dce8';               // glass glare
    p.set(i, y, col);
  }
  for (let y = 180; y <= 183; y++) for (let i = 386; i <= 394; i++) p.set(i, y, y === 180 ? '#3a6ac8' : i === 394 ? '#b8b4ac' : '#f0ece4');   // stacked bowls
  for (let y = 186; y <= 199; y++) for (let i = 422; i <= 446; i++) {
    const u = (i - 422) / 24;
    let col = dpick(['#ffffff', '#dcdce6', '#a8a8b8', '#6a6a7a', '#3a3a48'], 0.1 + Math.abs(u - 0.3) * 1.2, i, y, 0.6);
    if (y === 186) col = i > 424 && i < 444 ? '#7a3a1e' : '#e8e8f0';            // the broth at the rim
    if (y === 187) col = '#4a4a58';
    p.set(i, y, col);
  }
  for (let i = 440; i <= 450; i++) p.set(i, 184 - Math.round((i - 440) * 0.25), '#d8d8e4');   // the lid, pushed back
  for (let i = C0; i <= C1; i++) { p.set(i, 200, '#f0f0f8'); p.set(i, 201, '#8a8a98'); }
  for (let y = 202; y <= 231; y++) for (let i = C0 + 2; i <= C1 - 2; i++) {
    const L = poolAt(i, y), edge = y === 202 || y === 231 || i === C0 + 2 || i === C1 - 2, trim = y === 204 || y === 222 || i === C0 + 4 || i === C1 - 4;
    p.set(i, y, edge ? '#5a5a6a' : trim ? '#efe2c4' : dpick(['#d8483e', '#a82a2a', '#6a1a1e'], 0.6 - L * 0.7 + (y - 202) / 60, i, y, 0.6));
  }
  ptext(p, 'NOODLES', 402, 210, j => j < 2 ? '#fff4c8' : '#ffd060');
  for (const wx of [392, 438]) for (let y = 222; y < 250; y++) for (let i = wx - 9; i <= wx + 9; i++) {   // bicycle wheels
    const d = Math.hypot(i - wx, y - 236);
    if (d > 8.6) continue;
    if (d > 7.2) p.set(i, y, '#141418');
    else if (d < 1.2) p.set(i, y, '#c8c8d0');
    else if (Math.abs(Math.sin(Math.atan2(y - 236, i - wx) * 4)) < 0.16) p.set(i, y, '#7a7a88');
  }
  for (let y = 228; y <= 244; y++) for (let i = 356; i <= 366; i++) {             // a red plastic stool
    const top = y <= 229, leg = (i === 357 || i === 365) || (i === 361 && y > 236);
    if (top) p.set(i, y, y === 228 ? '#ff6a5a' : '#c8282a');
    else if (leg || y === 234) p.set(i, y, i < 361 ? '#b0242a' : '#7a161c');
  }
  // the basket of spare krathongs beside the steps
  for (let y = 230; y <= 236; y++) for (let i = GAP.x0 + 1; i <= GAP.x0 + 8; i++) p.set(i, y, y === 230 ? '#c8a060' : ((i + y) & 1) ? '#8a6030' : '#6a4424');
  paintRows(p, ['.oOo.pPp', 'lLlLlLlL'], { o: '#ffa21e', O: '#ffd040', p: '#ff5a96', P: '#ffb0d0', l: '#2e7a34', L: '#62b84c' }, GAP.x0 + 1, 228);
  // the lamp post on the right: a lantern on a dark green post (geckos and moths are drawn per frame)
  for (let y = LAMP.y + 5; y < 250; y++) { p.set(POST.x - 1, y, '#3a4a40'); p.set(POST.x, y, '#1e2a24'); p.set(POST.x + 1, y, '#121a16'); }
  for (let i = POST.x - 5; i <= POST.x + 5; i++) { p.set(i, POST.top, '#2a3a30'); p.set(i, LAMP.y + 4, '#2a3a30'); }
  for (let i = POST.x - 4; i <= POST.x + 4; i++) p.set(i, POST.top - 1, '#1e2a24');
  for (let y = POST.top + 1; y <= LAMP.y + 3; y++) for (let i = POST.x - 4; i <= POST.x + 4; i++)
    p.set(i, y, Math.abs(i - POST.x) === 4 ? '#2a3a30' : y < POST.top + 3 ? '#fff4c8' : Math.abs(i - POST.x) < 2 ? '#ffe6a0' : '#ffc860');
  return { c: p.done(), railBulbs: railBulbs.filter(b => p.own[b.y * W + b.x] !== 1), treeBulbs };
}

/* ================= Sprites: frangipani flowers, the couple, the dog, geckos ================= */
const FLOWER_ROWS = [
  ['wy', 'ww'],
  ['.w..', '.yww', 'wwy.', '..w.'],
  ['.ww...', '.ww.ww', '..yYww', 'wwyy..', 'ww.ww.', '...ww.'],
];
const FLOWER_KEYS = [{ w: '#fff8ec', y: '#ffd24a', Y: '#fff0a0' }, { w: '#ffb8cc', y: '#ffd860', Y: '#fff0b0' }];
const WOMAN = [
  ['....hhh....', '...hhfhh...', '...hHHHh...', '...hHHHh...', '...hhhhh...', '....sss....', '..ttTTTtt..', '.tttTTTttt.',
    '.tttTTTttt.', '.ttttTtttt.', '..ttttttt..', '..kkkkkkk..', '.kkKkkKkkk.', '.kkkkkkkkkk', 'kkKkkKkkKkk', 'kkkkkkkkkkk', '.pp.....pp.'],
  ['...........', '...........', '...........', '....hhh....', '...hhfhh...', '...hHHHh...', '..ttHHHtt..', '.tttTTTttt.',
    '.tttTTTttt.', '.ttTTTTTtt.', '.tttTTTttt.', '..kkkkkkk..', '.kkKkkKkkk.', '.kkkkkkkkkk', 'kkKkkKkkKkk', 'kkkkkkkkkkk', '.pp.....pp.'],
  ['....hhh....', '...hhfhh...', '..shHHHh...', '..shHHHh...', '...hhhhh...', '....sss....', '..ttTTTtt..', 'ssttTTTttt.',
    '.tttTTTttt.', '.ttttTtttt.', '..ttttttt..', '..kkkkkkk..', '.kkKkkKkkk.', '.kkkkkkkkkk', 'kkKkkKkkKkk', 'kkkkkkkkkkk', '.pp.....pp.'],
];
const MAN = [
  ['....hhhh....', '...hHHHHh...', '...hHHHHh...', '...hhhhhh...', '....ssss....', '..wwwwwwww..', '.wwwwwwwwww.', '.wwwwwwwwww.',
    '.wwwwwwwwww.', '.wwwwwwwwww.', '..wwwwwwww..', '..nnnnnnnn..', '.nnnnnnnnnn.', 'nnnNnnnnNnnn', 'nnnnnnnnnnnn', '.pp......pp.'],
  ['....hhhh....', '..shHHHHh...', '..shHHHHh...', '...hhhhhh...', '....ssss....', '..wwwwwwww..', '.wwwwwwwwww.', '.wwwwwwwwww.',
    '.wwwwwwwwww.', '.wwwwwwwwww.', '..wwwwwwww..', '..nnnnnnnn..', '.nnnnnnnnnn.', 'nnnNnnnnNnnn', 'nnnnnnnnnnnn', '.pp......pp.'],
];
const PEOPLE_KEY = { h: '#140c10', H: '#2e2228', f: '#fff4e0', s: '#a8704e', t: '#a83262', T: '#d8568a', k: '#40245e', K: '#c8a040',
  p: '#c08a6a', w: '#dedad2', W: '#c4c0bc', n: '#1c2240', N: '#2c3456' };
/** a figure seen from behind, backlit by the river: a warm rim wherever the outline meets the air */
function backlit(rows) {
  const p = painter(rows[0].length, rows.length), has = (i, j) => rows[j]?.[i] && rows[j][i] !== '.';
  rows.forEach((row, j) => { for (let i = 0; i < row.length; i++) if (has(i, j)) {
    let col = PEOPLE_KEY[row[i]];
    if (!has(i - 1, j) || !has(i + 1, j)) col = mixHex(col, '#ffc868', 'hH'.includes(row[i]) ? 0.18 : 0.4);
    else if (!has(i, j - 1)) col = mixHex(col, '#ffe0a8', 0.3);
    p.set(i, j, col);
  } });
  return p.done();
}
const DOG_BODY = [
  '....ee............................',
  '...eEEe......bbbbbbbbbbbb.........',
  '..eEEEEeb..bbBBBBBBBBBBBBbbb......',
  '..bBBBBBBbdbBBBBBBBBBBBBBBBBbb....',
  '.bBBBBBBBBdbbbbbbbbbbbbbbbbbbbb...',
  'bbbkkbbbbbdbbbbbbbbbbbbbbbbbbbbb..',
  'nbbbbbbbbbdbbbbbbbbbbbbbbbbbbbbbb.',
  '.bwwbbbbbbbbbbbbbbbbbbbbbbbbbbbbb.',
  '..wwwwwbbbbbbbbbbbbbbbbbbdbbbbbbb.',
  '.wwwwwwwwbbbbbbbbbbbbbbbbdbbbbbbt.',
  'wwwwwwwwwwddddddddddddddddddddtt..',
  '.ppp.ppp..........................',
];
// breathing: the flank rises a row on the in-breath
const DOG_ROWS = [DOG_BODY, DOG_BODY.map((r, j) => j === 0 ? '....ee.......bbbbbbbbbb...........' : j === 1 ? '...eEEe....bbBBBBBBBBBBbb.........' : r)];
const DOG_KEY = { b: '#b87a3e', B: '#e2aa62', e: '#6a3a1c', E: '#8a5230', w: '#f2e8d6', n: '#140c0c', k: '#3a2010', d: '#7a4a24', p: '#e8dcc8', t: '#a06a34' };
const GECKO = ['.b.', 'ebe', 'lbl', '.b.', 'lbl', '.t.', '.t.'];
const GECKO_KEY = { b: '#e6d0b0', e: '#1a1010', l: '#c8b090', t: '#d4bc9c' };

/* ================= Water, vignette ================= */
function renderWater() {
  const hh = EDGE - WL; const [c, x] = makeCanvas(W, hh);
  const P = ['#2c2448', '#241e40', '#1c1836', '#15122c', '#0e0c22'].map(hex);
  const img = x.createImageData(W, hh), d = img.data;
  for (let y = 0; y < hh; y++) for (let i = 0; i < W; i++) {
    const f = Math.pow(y / hh, 0.8) * (P.length - 1) * 0.999;
    let k = Math.floor(f);
    if (f - k > bayer(i, y)) k++;
    const col = P[Math.min(k, P.length - 1)], o = (y * W + i) * 4;
    d[o] = col[0]; d[o + 1] = col[1]; d[o + 2] = col[2]; d[o + 3] = 255;
  }
  x.putImageData(img, 0, 0);
  return c;
}
function renderVignette() {
  const [c, x] = makeCanvas(W, H); const img = x.createImageData(W, H), d = img.data;
  for (let y = 0; y < H; y++) for (let i = 0; i < W; i++) {
    const v = smooth(0.8, 1.5, Math.hypot((i - W / 2) / (W / 2), (y - H / 2) / (H / 2) * 0.9));
    const q = Math.floor(v * 4 + bayer(i, y)) / 4;
    if (q <= 0) continue;
    const o = (y * W + i) * 4; d[o] = 6; d[o + 1] = 2; d[o + 2] = 16; d[o + 3] = Math.round(q * 0.42 * 255);
  }
  x.putImageData(img, 0, 0);
  return c;
}

/* ================= Timings (loop seconds) and particle tables ================= */
const BARGES = { a: 8, b: 128, x0: 486, x1: -150 };       // tug and three rice barges, downstream (right to left), far lane
const EXPRESS = { a: 40, b: 64, x0: -80, x1: 486 };       // the orange-flag express boat, upstream, mid lane
const LONGTAILS = [{ a: 86, b: 98, dir: -1 }, { a: 176, b: 188, dir: 1 }];   // near lane, buzzing past
const CRUISE = { a: 136, b: 212, x0: 486, x1: -100 };      // the lit dinner cruise, downstream, mid lane
const PLANE = { a: 52, b: 142 };                          // an airliner letting down toward the airport, high and slow
const KRATHONG_T = 60, DRIFT = 26;                         // the couple lets a krathong go every minute; it drifts off in 26 s
const CHIMES = [30, 150];                                  // the spirit house's little bells ring in a breath of wind
const DOG_EAR = [23.5, 71.2, 118.8, 164.1, 209.6];
const DOG_TAIL = [44, 102, 147, 196, 231];
const GECKO_DART = [[46, 46.5, 55, 56.6], [130, 130.5, 138, 139.4], [196, 196.4, 203, 204.8]];   // up, stay, back down
const LANE_SPAN = W + 64;
const KR = [[], [], []], HY = [[], [], []];
{
  const r = rng(2468);
  // far / mid / near: clusters, members per cluster, rows they float on, cycles per loop (all drift downstream)
  [[8, 2, 4, 172, 187, 1, 5], [6, 2, 4, 189, 207, 2, 8], [4, 1, 3, 209, 232, 3, 13]].forEach(([n, m0, m1, y0, y1, k, dx], size) => {
    for (let c = 0; c < n; c++) {
      const ph = (c + r() * 0.7) / n, cy = y0 + r() * (y1 - y0), m = m0 + Math.floor(r() * (m1 - m0 + 1));
      for (let j = 0; j < m; j++) KR[size].push({ id: size * 100 + KR[size].length, ph, k, dx: Math.round(j * dx + r() * 4),
        y: Math.round(clamp(cy + (r() - 0.5) * (size + 1) * 3, y0, y1)), fl: Math.floor(r() * 4), bob: 17 + Math.floor(r() * 30), bp: r() });
    }
    for (let c = 0; c < [3, 3, 2][size]; c++) HY[size].push({ ph: r(), k, y: Math.round(y0 + r() * (y1 - y0)), bob: 13 + Math.floor(r() * 20), bp: r() });
  });
}
const FALL = [[], [], []];
{
  const r = rng(1357);
  [[9, 6, 9, 2, 26], [11, 9, 12, 4, 44], [6, 12, 15, 8, 70]].forEach(([count, nMin, nMax, amp, drift], size) => {
    for (let i = 0; i < count; i++) FALL[size].push({
      id: size * 100 + i, size, n: nMin + Math.floor(r() * (nMax - nMin + 1)), ph: r(), ks: 40 + Math.floor(r() * 40), p2: r(),
      kr: 20 + Math.floor(r() * 30), p3: r(), kf: 24 + Math.floor(r() * 30), p4: r(), amp: amp * (0.7 + r() * 0.6),
      drift: drift * (0.6 + r() * 0.8), pink: r() < 0.25 ? 1 : 0,
    });
  });
}
const FLOATING = [[196, 0.1], [204, 0.45], [214, 0.72], [224, 0.3], [184, 0.85]];   // fallen flowers drifting on the river: row, phase
const LANDED = [[208, 0], [236, 1], [262, 0], [297, 0], [338, 1]];                    // and a few lying on the rail
const STEAM = [], SMOKE = [], MOTHS = [];
{
  const r = rng(61);
  for (let k = 0; k < 14; k++) STEAM.push({ x: POT.x - 9 + Math.floor(r() * 19), n: 40 + Math.floor(r() * 16), ph: r(), k: 18 + Math.floor(r() * 20), p: r(), id: k, h: 9 + r() * 3 });
  for (let k = 0; k < 3; k++) SMOKE.push({ x: SH.x + 6 + k, n: 20 + k * 3, ph: r(), k: 11 + k * 4, p: r() });
  for (let k = 0; k < 7; k++) MOTHS.push({ lamp: k < 4 ? 0 : 1, k: (7 + Math.floor(r() * 9)) * (r() < 0.5 ? -1 : 1), ph: r(), rx: 4 + r() * 6, ry: 3 + r() * 4, kj: 300 + Math.floor(r() * 300) });
}
const CROWD = [];                                          // festival-goers on Wat Arun's riverfront, a few carrying candles
{
  const r = rng(777), SHIRTS = ['#e8e0d0', '#d84a5a', '#4a7ad8', '#f0c050', '#3a3a48', '#e87ab0'];
  for (let k = 0; k < 18; k++) {
    const seg = r() < 0.55 ? [112, 168] : [212, 264];
    CROWD.push({ seg, x: seg[0] + r() * (seg[1] - seg[0]), k: r() < 0.45 ? (r() < 0.5 ? -1 : 1) : 0, ph: r(), shirt: SHIRTS[Math.floor(r() * SHIRTS.length)],
      candle: r() < 0.45, tall: r() < 0.6 ? 1 : 0, id: k });
  }
}
const FAIRY = ['#ffe6a8', '#ffb454', '#ff7aa8', '#8affb0', '#8ac8ff', '#ffd84a'];
const LED = ['#ff4a6a', '#ffa03a', '#ffe04a', '#5aff9a', '#4ad0ff', '#8a6aff'];

// a picked weather (site/js/engine/weather.js): Bangkok's own frangipani (falling, afloat, on the rail) shows only with its own
// weather. Far drops end on the Thonburi bank and catch the floodlit temple (and the sign's bulbs), mid ones land on the
// river, near ones fall past the terrace through the cart's bulb, the lamp post, the spirit house and the fairy lights.
const WEATHER = weatherPlans(['frangipani']);
const WX_LAND = [[146, WL - 1], [WL + 2, EDGE - 1], [H + 4, H + 4]];
const WX_FAR = [{ x: PX, y: 92, rx: 40, ry: 66, col: '#ffd890' }, { x: PX, y: 142, rx: 62, ry: 16, col: '#ffd890' },
  { x: HALL.x, y: 142, rx: 20, ry: 18, col: '#ffb070' }, { x: 86, y: 158, rx: 24, ry: 12, col: '#fff0e0' }];
const WX_MID = [{ x: 190, y: 166, rx: 16, ry: 10, col: '#e0f0ff' }, { x: 300, y: 160, rx: 34, ry: 10, col: '#ffcf80' }];
const WX_NEAR = [{ x: BULB.x, y: BULB.y + 4, rx: 30, ry: 26, col: '#ffe0a0' }, { x: LAMP.x, y: LAMP.y + 4, rx: 22, ry: 30, col: '#ffd890' },
  { x: SH.x, y: 186, rx: 22, ry: 22, col: '#ffc070' }, { x: 100, y: 222, rx: 60, ry: 8, col: '#ffd8a0' }, { x: 300, y: 222, rx: 80, ry: 8, col: '#ffd8a0' }];
const wxDepths = far => [far, WX_MID, WX_NEAR].map(lights => ({ land: WX_LAND, lights }));
const WX = wxDepths(WX_FAR);
const WX_BB = wxDepths([...WX_FAR, { x: BB.x + (BB.w >> 1), y: BB.y + (BB.h >> 1), rx: 34, ry: 18, col: '#ffe8b0' }]);   // + the sign's bulbs

/* ================= Pre-render everything static ================= */
let HL, SKY_C, MOON_C, CLOUDS, FAR, BANK, FG, WATER_C, VIG_C, BB_FR_C, AD_C, PITCH, SPR;
const GLINTS = [];
function prerender() {
  HL = {
    moon: makeHalo(58, '#a8a8e0', 0.16, 1.6), moonCore: makeHalo(22, '#fff4d8', 0.24, 1.4),
    temple: makeHalo(86, '#ffa848', 0.15, 1.6), prang: makeHalo(36, '#ffd890', 0.12, 1.5), sat: makeHalo(16, '#ffc070', 0.14, 1.5),
    ubosot: makeHalo(30, '#ff9a50', 0.14, 1.5), fort: makeHalo(26, '#fff0e0', 0.1, 1.5),
    flood: makeHalo(6, '#fff0c0', 0.55, 1.2), lamp: makeHalo(5, '#ffd890', 0.42, 1.2), beacon: makeHalo(4, '#ff3a3a', 0.6, 1),
    kr: makeHalo(4, '#ffb050', 0.5, 1.2), krNear: makeHalo(8, '#ffa040', 0.45, 1.3),
    cart: makeHalo(44, '#ffd08a', 0.2, 1.5), bulb: makeHalo(10, '#fff0c0', 0.6, 1.2), post: makeHalo(36, '#ffc070', 0.2, 1.5),
    postCore: makeHalo(9, '#fff0c0', 0.5, 1.2), candle: makeHalo(9, '#ffb050', 0.5, 1.3), shrine: makeHalo(30, '#ffa040', 0.16, 1.5),
    fairy: FAIRY.map(c => makeHalo(4, c, 0.42, 1.2)), held: makeHalo(14, '#ffb050', 0.42, 1.4),
    boat: makeHalo(30, '#ffd8a0', 0.14), boatLamp: makeHalo(4, '#ffe0a0', 0.55, 1.2), cool: makeHalo(24, '#c8f0e0', 0.12),
    bbSky: makeHalo(38, '#ffe0a0', 0.1, 1.5), bbBulb: makeHalo(3, '#ffe8a0', 0.5, 1),
    plane: makeHalo(3, '#ff4040', 0.6, 1), strobe: makeHalo(5, '#ffffff', 0.7, 1),
  };
  SKY_C = renderSky(); MOON_C = renderMoon(); CLOUDS = CLOUD_DEFS.map(makeCloudLayer);
  FAR = renderFarCity(); GLINTS.length = 0; BANK = renderBank(GLINTS);
  FG = renderForeground(); WATER_C = renderWater(); VIG_C = renderVignette();
  BB_FR_C = renderBillboardFrame(); AD_C = AD_LINES.map(renderAd); PITCH = makePitch(BB.w, BB.h, { accent: '#ffe08a' });
  const lt = makeLongtail(), cr = makeCruise();
  SPR = {
    longtail: [lt, flipCanvas(lt)], express: makeExpress(), cruise: flipCanvas(cr), tug: makeTug(), barge: [0, 1, 2].map(makeBarge),
    ferry: makeFerry(), kr: [0, 1, 2].map(s => KFLOW.map(fl => makeKrathong(s, fl))), hy: [0, 1, 2].map(makeHyacinth),
    fl: FLOWER_ROWS.map(rows => FLOWER_KEYS.map(key => sprite(rows, key))),
    woman: WOMAN.map(backlit), man: MAN.map(backlit), dog: DOG_ROWS.map(rows => sprite(rows, DOG_KEY)), gecko: sprite(GECKO, GECKO_KEY),
  };
}

/* ================= Per-frame drawing: sky, far city, temple ================= */
function drawSky(t) {
  ctx.drawImage(SKY_C, 0, 0);
  ctx.fillStyle = '#e6e8ff';
  for (const s of STARS) {
    const a = s.b * (0.5 + 0.5 * wave(t, s.k, s.ph));
    ctx.globalAlpha = a; ctx.fillRect(s.x, s.y, 1, 1);
    if (s.big) { ctx.globalAlpha = a * 0.4; ctx.fillRect(s.x - 1, s.y, 3, 1); ctx.fillRect(s.x, s.y - 1, 1, 3); }
  }
  ctx.globalAlpha = 1;
  const cloud = L => { const off = Math.floor(frac(L.k * t / LOOP) * W); ctx.drawImage(L.c, off - W, L.y); ctx.drawImage(L.c, off, L.y); };
  ctx.globalAlpha = 0.78;
  cloud(CLOUDS[0]);                                                              // high wisps, silvered by the moon's glow
  ctx.globalAlpha = 1;
  halo(HL.moon, MOON.x, MOON.y, 0.95); halo(HL.moonCore, MOON.x, MOON.y, 0.8);
  ctx.drawImage(MOON_C, MOON.x - MOON.r - 1, MOON.y - MOON.r - 1);
  ctx.globalAlpha = 0.78;
  cloud(CLOUDS[1]);                                                              // the low haze bands, over the city's glow
  ctx.globalAlpha = 1;
}
function drawPlane(t) {
  if (t < PLANE.a || t >= PLANE.b) return;
  const f = (t - PLANE.a) / (PLANE.b - PLANE.a), x = Math.round(lerp(492, -12, f)), y = Math.round(lerp(58, 70, f));
  ctx.fillStyle = '#403a58'; ctx.fillRect(x - 2, y, 6, 1); ctx.fillRect(x + 1, y - 1, 1, 1); ctx.fillRect(x + 3, y - 1, 1, 1);
  ctx.fillStyle = '#fff4d0'; ctx.fillRect(x - 3, y, 1, 1);                     // landing light on the nose
  if (frac(t) < 0.5) { ctx.fillStyle = '#ff5050'; ctx.fillRect(x + 1, y + 1, 1, 1); halo(HL.plane, x + 1, y + 1, 0.8); }
  if (frac(t / 1.25) < 0.07) { ctx.fillStyle = '#ffffff'; ctx.fillRect(x + 4, y, 1, 1); halo(HL.strobe, x + 4, y, 0.9); }
}
function drawFar(t) {
  ctx.drawImage(FAR.c, 0, 0);
  ctx.globalAlpha = 0.6;
  for (const l of FAR.lights) if (hash(l.id, Math.floor(frac(t / LOOP + l.off) * l.n), 7) < l.p) { ctx.fillStyle = l.col; ctx.fillRect(l.x, l.y, 1, 1); }
  ctx.globalAlpha = 1;
  for (const b of FAR.beacons) if (frac(t / 1.5 + b.ph) < 0.5) { ctx.fillStyle = '#ff4040'; ctx.fillRect(b.x, b.y, 1, 1); halo(HL.beacon, b.x, b.y, 0.8); }
}
function drawGlow(t) {
  const br = 0.94 + 0.06 * wave(t, 4);
  halo(HL.temple, PX, 112, br); halo(HL.prang, PX, 66, br);
  halo(HL.ubosot, HALL.x, 140, 0.8); halo(HL.fort, 86, 156, 0.8);
}
const FLOODS = [[PX - 30, 151], [PX - 13, 151], [PX + 13, 151], [PX + 30, 151], [PX - 52, 158], [PX + 52, 158], [HALL.x, 158]];
function drawCrowd(t) {
  const s = step(t, 1800);
  for (const c of CROWD) {
    const len = c.seg[1] - c.seg[0];
    const x = Math.round(c.k ? c.seg[0] + frac(c.k * t / LOOP + c.ph) * len : c.x), y = 161 - c.tall;
    ctx.fillStyle = c.shirt; ctx.fillRect(x, y - 1, 1, 2);
    ctx.fillStyle = '#1a1014'; ctx.fillRect(x, y - 2, 1, 1);
    if (c.candle) { ctx.fillStyle = hash(c.id, s, 5) < 0.2 ? '#ffb040' : '#fff0b0'; ctx.fillRect(x + 1, y - 1, 1, 1); }
  }
}
function drawBankFx(t) {
  for (const w of BANK.wins) if (hash(w.id, Math.floor(frac(t / LOOP + w.off) * w.n), 3) < w.p) { ctx.fillStyle = w.col; ctx.fillRect(w.x, w.y, 1, 1); }
  BANK.lamps.forEach((l, i) => halo(HL.lamp, l.x, l.y, (l.dim ? 0.4 : 0.72) + 0.2 * wave(t, 500 + i * 37)));
  ctx.fillStyle = '#fffbe8';
  for (const [x, y] of FLOODS) { ctx.fillRect(x, y, 1, 1); halo(HL.flood, x, y, 0.8); }
  halo(HL.sat, SATS[0], 128, 0.8); halo(HL.sat, SATS[1], 128, 0.8);
  const s = step(t, 2400);                                                       // bits of porcelain catching the light
  for (const g of GLINTS) { const h = hash(g.id, s, 21); if (h < 0.03) { ctx.fillStyle = PORC[Math.floor(h * 1000) % 5]; ctx.fillRect(g.x, g.y, 1, 1); } }
  if (frac(t / 2) < 0.5) { ctx.fillStyle = '#ff3a3a'; ctx.fillRect(86, 123, 1, 1); halo(HL.beacon, 86, 123, 0.9); }   // the fort's flagpole
  drawCrowd(t);
}

/* ================= The river ================= */
const STREAKS = [
  { x: PX, w: 16, col: '#ffd070', a: 0.4, seed: 1 }, { x: SATS[0], w: 6, col: '#ffc060', a: 0.3, seed: 2 },
  { x: SATS[1], w: 6, col: '#ffc060', a: 0.3, seed: 3 }, { x: HALL.x, w: 6, col: '#ffa860', a: 0.24, seed: 4 },
  { x: 86, w: 6, col: '#f0e8e0', a: 0.2, seed: 5 }, { x: MOON.x, w: 9, col: '#f4f0dc', a: 0.42, seed: 6 },
  { x: 300, w: 10, col: '#ffcf80', a: 0.22, seed: 7 }, { x: 416, w: 14, col: '#ffc070', a: 0.2, seed: 8 },
  { x: 190, w: 3, col: '#e0f0ff', a: 0.3, seed: 9 },
];
function drawWater(t, billboard) {
  ctx.drawImage(WATER_C, 0, WL);
  reflect(ctx, t, { top: WL, rows: EDGE - WL, squash: 1.45, alpha: 0.6, amp: 0.5, grow: 0.045 });
  rect(ctx, 0, WL, W, EDGE - WL, 'rgba(12,8,36,0.14)');
  rect(ctx, 0, WL, W, 1, '#0e0a22');
  for (const s of STREAKS) streak(ctx, t, s.x, s.w, s.col, s.a, s.seed, WL + 1, EDGE);
  if (billboard) streak(ctx, t, BB.x + (BB.w >> 1), 8, '#ffe8b0', 0.2, 11, WL + 1, EDGE);
  const gs = step(t, 1440);
  for (let k = 0; k < 48; k++) {
    if (hash(k, gs, 17) < 0.35) continue;
    const x = Math.floor(hash(k, gs, 18) * W), y = WL + 2 + Math.floor(hash(k, gs, 19) * (EDGE - WL - 4));
    const warm = Math.abs(x - PX) < 22 ? 2 : Math.abs(x - MOON.x) < 8 ? 1 : 0;
    ctx.globalAlpha = warm ? 0.85 : 0.4; ctx.fillStyle = warm === 2 ? '#ffe2a0' : warm ? '#f8f4e8' : '#8a90d0';
    ctx.fillRect(x, y, warm ? 2 : 1, 1);
  }
  ctx.globalAlpha = 1;
}
const laneX = (k, ph, t) => W + 12 - frac(k * t / LOOP + ph) * LANE_SPAN;
/** one krathong on its waterline y: the flame's broken reflection, the float, the candle flame and incense, a glow */
function drawKrathong(size, c, x, y, s, id) {
  const [cx, cy] = KR_CANDLE[size], w = c.width, top = y - c.height + 1, fx = x + cx, fy = top + cy, fl = hash(id, s, 31);
  ctx.globalAlpha = 0.35; ctx.fillStyle = '#10180e'; ctx.fillRect(x + 1, y + 1, w - 2, 1);
  ctx.fillStyle = '#ffb050';
  for (let j = 1; j <= size + 2; j++) {
    ctx.globalAlpha = 0.55 * (1 - j / (size + 3));
    ctx.fillRect(fx - (size > 1 && (j & 1) ? 1 : 0), y + j, size > 1 && j < 3 ? 2 : 1, 1);
  }
  ctx.globalAlpha = 1;
  ctx.drawImage(c, x, top);
  ctx.fillStyle = fl < 0.15 ? '#ffd070' : '#fff4c0'; ctx.fillRect(fx, fy, 1, 1);
  if (size > 0 && fl > 0.3) { ctx.fillStyle = '#ffa030'; ctx.fillRect(fx, fy - 1, 1, 1); }
  if (size === 2) { ctx.fillStyle = hash(id, s >> 3, 32) < 0.5 ? '#ff6a3a' : '#c83a1a'; ctx.fillRect(x + 2, top, 1, 1); ctx.fillRect(x + 6, top, 1, 1); }
  if (size) halo(size === 2 ? HL.krNear : HL.kr, fx, fy, 0.6 + 0.3 * fl);
}
function drawKrathongs(size, t, y0, y1) {
  const spr = SPR.kr[size], s = step(t, 2400);
  for (const k of KR[size]) {
    if (k.y < y0 || k.y >= y1) continue;
    const c = spr[k.fl], x = Math.round(laneX(k.k, k.ph, t) + k.dx);
    if (x < -c.width || x > W) continue;
    drawKrathong(size, c, x, k.y + (size ? Math.round(wave(t, k.bob, k.bp) * 0.6) : 0), s, k.id);
  }
}
function drawHyacinths(size, t, y0, y1) {
  const c = SPR.hy[size], w = c.width, h = c.height;
  for (const q of HY[size]) {
    if (q.y < y0 || q.y >= y1) continue;
    const x = Math.round(laneX(q.k, q.ph, t)), y = q.y + Math.round(wave(t, q.bob, q.bp) * 0.5);
    if (x < -w || x > W) continue;
    ctx.globalAlpha = 0.3; ctx.fillStyle = '#060c08'; ctx.fillRect(x + 1, y + 1, w - 2, 1); ctx.globalAlpha = 1;
    ctx.drawImage(c, x, y - h + 1);
  }
}
function drawFloating(t) {                                                      // fallen frangipani riding the current
  for (const [y, ph] of FLOATING) {
    const x = Math.round(laneX(y > 208 ? 3 : 2, ph, t)), c = SPR.fl[y > 208 ? 1 : 0][ph > 0.6 ? 1 : 0];
    if (x > -4 && x < W) ctx.drawImage(c, x, y - c.height + 1);
  }
}
/** a boat on waterline wl (its sprite row wlRow sits on it): its rippled reflection first, then the boat */
function boatAt(spr, x, wl, wlRow, t, rows, a) {
  const w = spr.width;
  for (let j = 0; j < rows; j++) {
    const sy = wlRow - j;
    if (sy < 0 || wl + 1 + j >= EDGE) break;
    const off = Math.round(Math.sin(TAU * 40 * t / LOOP + j * 0.7) * (0.5 + j * 0.15));
    ctx.globalAlpha = a * (1 - j / rows);
    ctx.drawImage(spr, 0, sy, w, 1, x + off, wl + 1 + j, w, 1);
  }
  ctx.globalAlpha = 1;
  ctx.drawImage(spr, x, wl - wlRow);
}
function drawWake(x0, dir, wl, len, t, spread) {                                // foam trailing behind a stern at x0
  const s = step(t, 1200);
  ctx.fillStyle = '#eef0ff';
  for (let d = 1; d < len; d++) {
    const xx = x0 - dir * d;
    if (xx < 0 || xx >= W) continue;
    const fade = 1 - d / len;
    if (hash(d, s, 41) < 0.8 * fade) { ctx.globalAlpha = 0.8 * fade; ctx.fillRect(xx, wl + (hash(d, s, 44) < 0.5 ? 0 : 1), 1, 1); }
    const yn = wl + 1 + Math.round(d * spread);
    if (yn < EDGE && hash(d, s, 42) < 0.6) { ctx.globalAlpha = 0.5 * fade; ctx.fillRect(xx, yn, 1, 1); }
  }
  ctx.globalAlpha = 1;
}
function drawFerry(t) {                                                         // the cross-river ferry, waiting at the pier
  const wl = 172 + Math.round(wave(t, 36) * 0.6);
  boatAt(SPR.ferry, 181, wl, 8, t, 4, 0.3);
  halo(HL.cool, 194, wl - 5, 0.5);
}
function drawBarges(t) {
  if (t < BARGES.a || t >= BARGES.b) return;
  const X = Math.round(lerp(BARGES.x0, BARGES.x1, (t - BARGES.a) / (BARGES.b - BARGES.a))), wl = LANE.far;
  const xs = [X, X + 26, X + 64, X + 102];
  ctx.fillStyle = '#1a1418';                                                    // tow lines, sagging toward the water
  for (let k = 0; k < 3; k++) {
    const a = xs[k] + (k ? 32 : 20), b = xs[k + 1];
    for (let i = a; i < b; i++) ctx.fillRect(i, wl - 4 + Math.round(Math.sin(Math.PI * (i - a) / (b - a)) * 1.5), 1, 1);
  }
  boatAt(SPR.tug, X, wl, 11, t, 6, 0.3);
  for (let k = 0; k < 3; k++) boatAt(SPR.barge[k], xs[k + 1], wl + Math.round(wave(t, 30 + k * 3, k * 0.3) * 0.5), 8, t, 6, 0.3);
  ctx.fillStyle = '#ffffff'; ctx.fillRect(X + 8, wl - 11, 1, 1); halo(HL.boatLamp, X + 8, wl - 11, 0.9);   // masthead light
  ctx.fillStyle = '#ff4040'; ctx.fillRect(X + 4, wl - 6, 1, 1);
  for (let k = 0; k < 3; k++) if (k !== 1) halo(HL.boatLamp, xs[k + 1] + 27, wl - 6, 0.8);
  streak(ctx, t, X + 9, 3, '#ffd890', 0.3, 13, wl + 1, EDGE);
}
function drawExpress(t) {
  if (t < EXPRESS.a || t >= EXPRESS.b) return;
  const x = Math.round(lerp(EXPRESS.x0, EXPRESS.x1, (t - EXPRESS.a) / (EXPRESS.b - EXPRESS.a))), wl = LANE.mid, y = wl - 16;
  boatAt(SPR.express, x, wl, 16, t, 10, 0.32);
  drawWake(x + 2, 1, wl, 60, t, 0.1);
  ctx.fillStyle = '#eef0ff'; ctx.globalAlpha = 0.8;                              // the bow wave
  for (let k = 0; k < 5; k++) if (hash(k, step(t, 1200), 45) < 0.8) ctx.fillRect(x + 72 + (k >> 1), wl - (k & 1), 1, 1);
  ctx.globalAlpha = 1;
  const fr = step(t, 1440) % 3;                                                 // the orange flag flying from the stern
  ctx.fillStyle = '#ff8a1e'; ctx.fillRect(x + 6, y, 3, 1); ctx.fillRect(x + 6, y + 1, fr === 1 ? 2 : 3, 1);
  ctx.fillStyle = '#ffb04a'; ctx.fillRect(x + 6 + (fr === 2 ? 1 : 0), y, 1, 1); if (fr === 0) ctx.fillRect(x + 9, y + 1, 1, 1);
  ctx.fillStyle = '#ffffff'; ctx.fillRect(x + 62, y - 1, 1, 1); halo(HL.boatLamp, x + 62, y - 1, 0.8);
  halo(HL.cool, x + 36, y + 8, 0.7);
  streak(ctx, t, x + 22, 4, '#c8f0e0', 0.24, 14, wl + 1, EDGE); streak(ctx, t, x + 48, 4, '#c8f0e0', 0.24, 15, wl + 1, EDGE);
}
function drawCruise(t) {
  if (t < CRUISE.a || t >= CRUISE.b) return;
  const spr = SPR.cruise, w = spr.width, wl = LANE.mid + 1, y = wl - 23;
  const x = Math.round(lerp(CRUISE.x0, CRUISE.x1, (t - CRUISE.a) / (CRUISE.b - CRUISE.a)));
  boatAt(spr, x, wl, 23, t, 12, 0.36);
  drawWake(x + w - 4, -1, wl, 40, t, 0.08);
  for (const o of [16, 40, 64, 84]) streak(ctx, t, x + o, 5, '#ffc870', 0.3, o, wl + 1, EDGE);
  const s = step(t, 480);                                                       // the LED strip along the hull, colours rolling
  for (let i = 6; i < w - 4; i += 2) { ctx.fillStyle = LED[((i >> 1) + s) % LED.length]; ctx.fillRect(x + i, y + 19, 2, 1); }
  const s2 = step(t, 720);                                                      // bulbs under the canopy, twinkling
  for (let i = 12; i < 80; i += 3) { ctx.fillStyle = hash(i, s2, 5) > 0.2 ? '#fff0b0' : '#a08050'; ctx.fillRect(x + w - 1 - i, y + 4, 1, 1); }
  halo(HL.boat, x + (w >> 1), y + 12, 0.8);
}
function drawLongtail(L, t) {
  if (t < L.a || t >= L.b) return;
  const f = (t - L.a) / (L.b - L.a), spr = SPR.longtail[L.dir > 0 ? 0 : 1], w = spr.width;
  const x = Math.round(L.dir > 0 ? lerp(-100, W + 30, f) : lerp(W + 30, -100, f)), wl = LANE.near + Math.round(wave(t, 400) * 0.6);
  const prop = L.dir > 0 ? x : x + w - 1;                                       // the propeller, at the far end of the shaft
  boatAt(spr, x, wl, 13, t, 8, 0.3);
  drawWake(prop, L.dir, wl, 70, t, 0.1);
  ctx.fillStyle = '#f4f6ff';                                                    // the rooster tail thrown up behind it
  for (let k = 0; k < 40; k++) {
    const u = frac(t * 3 + k / 40), d = u * 30, hgt = Math.sin(Math.PI * Math.min(1, u * 1.15)) * 9 * (0.5 + 0.5 * hash(k, 3));
    ctx.globalAlpha = 0.9 * (1 - u);
    ctx.fillRect(Math.round(prop - L.dir * d), Math.round(wl + 1 - hgt), k % 3 ? 1 : 2, 1);
  }
  ctx.globalAlpha = 1;
  halo(HL.boat, x + (w >> 1), wl - 9, 0.5);
  streak(ctx, t, x + (w >> 1), 8, '#ffe0a0', 0.25, 15, wl + 1, EDGE);
}
function drawCoupleKrathong(t) {                                               // theirs, held at the water, then drifting away
  const u = t % KRATHONG_T, s = step(t, 2400);
  if (u < 3 || u >= 7 + DRIFT) return;
  if (u < 7) { drawKrathong(2, SPR.kr[2][1], 162, 230, s, 999); return; }
  const f = (u - 7) / DRIFT, e = f * f * 0.25 + f * 0.75, size = f < 0.45 ? 2 : 1;
  drawKrathong(size, SPR.kr[size][1], Math.round(lerp(162, -12, e)), Math.round(230 - 12 * smooth(0, 0.8, f)), s, 999);
}

/* ================= Frangipani flowers falling (far ones end on the far bank, mid ones on the river) ================= */
function drawFalling(size, t) {
  const spr = SPR.fl[size], S = FLOWER_ROWS[size], w = S[0].length, h = S.length;
  for (const b of FALL[size]) {
    const q = t * b.n / LOOP + b.ph, cyc = Math.floor(q) % b.n, u = q - Math.floor(q), sw = wave(t, b.ks, b.p2);
    const end = size === 2 ? H + 12 : size === 0 ? 148 + hash(b.id, cyc, 7) * 20 : WL + 6 + hash(b.id, cyc, 7) * (EDGE - WL - 12);
    const fall = size === 2 ? u : Math.min(1, u / 0.86), falling = fall < 1;
    const y = -8 + fall * (end + 8) - (falling ? Math.abs(sw) * (size + 1) * 1.2 : 0);
    const x = -30 + hash(b.id, cyc, 5) * (W + 40) + fall * b.drift + (falling ? sw * b.amp : 0) - (falling ? 0 : (u - 0.86) * 30);
    if (x < -8 || x > W + 8) continue;
    const c = spr[b.pink];
    ctx.globalAlpha = (size < 2 && !falling ? (1 - u) / 0.14 : 1) * (size === 0 ? 0.7 : 1);
    if (size < 2) { ctx.drawImage(c, Math.round(x), Math.round(y)); continue; }
    const fl = Math.cos(TAU * (b.kf * t / LOOP + b.p4));
    ctx.save();
    ctx.translate(Math.round(x), Math.round(y)); ctx.rotate(sw * 0.8 + 0.4 * wave(t, b.kr, b.p3)); ctx.scale(1, Math.max(0.3, Math.abs(fl)));
    ctx.drawImage(c, -(w >> 1), -(h >> 1));
    ctx.restore();
  }
  ctx.globalAlpha = 1;
}

/* ================= The terrace, per frame ================= */
function drawFairyLights(t) {
  const s = step(t, 480);
  const bulb = (b, k) => {
    const I = 0.5 + 0.5 * Math.sin(TAU * 24 * t / LOOP - k * 0.7), off = hash(k, s, 13) < 0.04, ci = k % FAIRY.length;
    ctx.globalAlpha = off ? 0.35 : 0.55 + 0.45 * I; ctx.fillStyle = FAIRY[ci]; ctx.fillRect(b.x, b.y, 1, 1);
    ctx.globalAlpha = 1;
    if (!off && k % 3 === 0) halo(HL.fairy[ci], b.x, b.y, 0.35 + 0.5 * I);
  };
  FG.railBulbs.forEach(bulb);
  FG.treeBulbs.forEach((b, k) => bulb(b, k + 7));
}
function drawShrine(t) {
  const S = SH.x, s = step(t, 1800);
  halo(HL.shrine, S, 186, 0.85 + 0.1 * wave(t, 7));
  for (const [dx, seed] of [[-3, 1], [3, 2]]) {                                 // two candles on the tray
    const f = hash(seed, s, 7);
    ctx.fillStyle = '#fff4c0'; ctx.fillRect(S + dx, 189, 1, 1);
    if (f > 0.3) { ctx.fillStyle = '#ffa030'; ctx.fillRect(S + dx, 188, 1, 1); }
    halo(HL.candle, S + dx, 188, 0.7 + 0.3 * f);
  }
  for (let k = 0; k < 3; k++) {                                                 // incense: glowing tips, a thread of smoke
    ctx.fillStyle = hash(k, s >> 2, 9) < 0.5 ? '#ff5a3a' : '#ff8a4a'; ctx.fillRect(S + 6 + k, 189, 1, 1);
    const sm = SMOKE[k], uq = frac(t * sm.n / LOOP + sm.ph);
    for (let j = 0; j < 3; j++) {
      const u = frac(uq + j / 3), x = Math.round(sm.x + wave(t, sm.k, sm.p + j * 0.2) * 2 * u + Math.sin(u * 5 + k) * u * 1.5);
      ctx.globalAlpha = 0.35 * (1 - u) * Math.min(1, u * 8); ctx.fillStyle = '#e8e0f0'; ctx.fillRect(x, Math.round(188 - u * 22), 1, 1);
    }
  }
  ctx.globalAlpha = 1;
  let swing = 0;                                                                // the little bells at the eaves, ringing in the wind
  for (const c of CHIMES) if (t >= c && t < c + 3.5) swing = Math.round(Math.sin((t - c) * 9) * (1 - (t - c) / 3.5) * 1.4);
  ctx.fillStyle = '#f0c050';
  for (const x of [S - 13, S + 13]) { ctx.fillRect(x + swing, 177, 1, 2); ctx.fillStyle = '#fff0a0'; ctx.fillRect(x + swing, 179, 1, 1); ctx.fillStyle = '#f0c050'; }
}
function drawCart(t) {
  halo(HL.cart, BULB.x, 194, 0.9);
  ctx.fillStyle = '#fff8e0'; ctx.fillRect(BULB.x - 1, BULB.y - 1, 3, 2); ctx.fillStyle = '#ffffff'; ctx.fillRect(BULB.x, BULB.y - 1, 1, 1);
  halo(HL.bulb, BULB.x, BULB.y, 0.95 + 0.05 * wave(t, 1200));
  ctx.fillStyle = '#fffaf4';                                                    // steam off the broth
  for (const s of STEAM) {
    const u = frac(t * s.n / LOOP + s.ph);
    const x = Math.round(s.x + wave(t, s.k, s.p) * 1.6 * u + Math.sin(u * 7 + s.id) * 1.3 * u);
    const a = 0.85 * Math.min(1, u * 5) * (1 - u), y = Math.round(POT.y - 1 - u * s.h);
    ctx.globalAlpha = a; ctx.fillRect(x, y, 1, 2);
    if (u > 0.25) { ctx.globalAlpha = a * 0.55; ctx.fillRect(x + (s.id & 1 ? 1 : -1), y - 1, 1, 2); }
  }
  ctx.globalAlpha = 1;
}
function drawLampPost(t) {
  const f = 0.93 + 0.07 * wave(t, 1500, 0.2);
  halo(HL.post, LAMP.x, LAMP.y, f); halo(HL.postCore, LAMP.x, LAMP.y, f);
  ctx.fillStyle = '#e6e0d8'; ctx.globalAlpha = 0.9;                            // moths round both bulbs
  for (const m of MOTHS) {
    const lx = m.lamp ? LAMP.x : BULB.x, ly = m.lamp ? LAMP.y + 1 : BULB.y + 1, a = TAU * (m.k * t / LOOP + m.ph), j = hash(m.kj, step(t, m.kj), 3);
    ctx.fillRect(Math.round(lx + Math.cos(a) * m.rx + (j - 0.5) * 2), Math.round(ly + Math.sin(a * 2) * m.ry * 0.6 + Math.sin(a) * 2), 1, 1);
  }
  ctx.globalAlpha = 1;
  let gy = 166;                                                                 // geckos on the post, one darting up after the moths
  for (const [a, b, c, d] of GECKO_DART) {
    if (t >= a && t < b) gy = lerp(166, 154, smooth(a, b, t));
    else if (t >= b && t < c) gy = 154 + (wave(t, 480) > 0.8 ? 1 : 0);
    else if (t >= c && t < d) gy = lerp(154, 166, smooth(c, d, t));
  }
  ctx.drawImage(SPR.gecko, POST.x - 1, Math.round(gy));
  ctx.drawImage(SPR.gecko, POST.x - 1, 188 + (wave(t, 6, 0.3) > 0.7 ? -1 : 0));
}
function drawCouple(t) {
  const u = t % KRATHONG_T, held = u < 3 || u >= 55;
  const wf = u >= 3 && u < 8 ? 1 : u >= 52 && u < 55 ? 2 : 0, mf = (u >= 14 && u < 20) || (u >= 40 && u < 44) ? 1 : 0;
  const wS = SPR.woman[wf], mS = SPR.man[mf];
  ctx.drawImage(wS, COUPLE.w, EDGE - wS.height);
  ctx.drawImage(mS, COUPLE.m, EDGE - mS.height);
  if (held) halo(HL.held, COUPLE.w + 5, EDGE - 10, 0.7 + 0.25 * hash(5, step(t, 1800), 3));
}
function drawDog(t) {
  const pose = wave(t, 60, 0.2) > 0.1 ? 1 : 0;
  ctx.drawImage(SPR.dog[pose], DOG.x, DOG.y);
  if (DOG_EAR.some(e => t >= e && t < e + 0.3)) { ctx.fillStyle = DOG_KEY.e; ctx.fillRect(DOG.x + 3, DOG.y - 1, 2, 1); ctx.fillRect(DOG.x + 2, DOG.y, 1, 1); }
  let lift = 0;
  for (const f of DOG_TAIL) { const d = t - f; if (d >= 0 && d < 1.4) lift = Math.round(2 * Math.abs(Math.sin(d / 1.4 * Math.PI * 3)) * (1 - d / 1.4)); }
  ctx.fillStyle = DOG_KEY.t; ctx.fillRect(DOG.x + 32, DOG.y + 9 - lift, 1, 1); ctx.fillRect(DOG.x + 33, DOG.y + 8 - lift * 2, 1, 1);
  ctx.fillStyle = DOG_KEY.B; ctx.fillRect(DOG.x + 33, DOG.y + 7 - lift * 2, 1, 1);
}
function drawForeground(t, own) {
  ctx.drawImage(FG.c, 0, 0);
  if (own) for (const [x, k] of LANDED) {                                       // frangipani that have landed on the rail
    const key = FLOWER_KEYS[k];
    rect(ctx, x, RAIL - 1, 2, 1, key.w); px(ctx, x + 1, RAIL - 2, key.w); px(ctx, x, RAIL - 2, key.y);
  }
  drawFairyLights(t);
  drawShrine(t);
  drawCart(t);
  drawLampPost(t);
  drawCouple(t);
  drawDog(t);
}

/* ================= Frame compositor (back to front) ================= */
// billboard: false leaves the sign out (the loop videos); weather: a WEATHERS pick (site/js/engine/weather.js), the city's
// own when left out
function renderFrame(t, { pitch, billboard = true, weather, reducedMotion = false } = {}) {
  t = ((t % LOOP) + LOOP) % LOOP;
  ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.globalAlpha = 1; ctx.globalCompositeOperation = 'source-over';
  const wx = WEATHER(weather), wxc = billboard ? WX_BB : WX, own = !!wx.frangipani;
  drawSky(t);
  drawPlane(t);
  drawFar(t);
  if (own) drawFalling(0, t);                                                   // far flowers pass behind the temple
  drawGlow(t);
  if (billboard) drawBillboard(t, pitch);                                       // on the shophouse roof
  ctx.drawImage(BANK.c, 0, 0);
  drawBankFx(t);
  drawWeather(ctx, wx.shared, 0, t, wxc[0], reducedMotion);
  drawWater(t, billboard);                                                      // then everything on the river, far to near
  drawFerry(t);
  drawKrathongs(0, t, 0, LANE.far + 2); drawHyacinths(0, t, 0, LANE.far + 2);   // floats close to a boat's lane pass behind it
  drawBarges(t);
  drawKrathongs(0, t, LANE.far + 2, H); drawHyacinths(0, t, LANE.far + 2, H);
  drawKrathongs(1, t, 0, LANE.mid + 4); drawHyacinths(1, t, 0, LANE.mid + 4);
  drawExpress(t); drawCruise(t);
  drawKrathongs(1, t, LANE.mid + 4, H); drawHyacinths(1, t, LANE.mid + 4, H);
  if (own) drawFloating(t);
  drawWeather(ctx, wx.shared, 1, t, wxc[1], reducedMotion);
  drawKrathongs(2, t, 0, LANE.near + 5); drawHyacinths(2, t, 0, LANE.near + 5);
  for (const L of LONGTAILS) drawLongtail(L, t);
  drawKrathongs(2, t, LANE.near + 5, H); drawHyacinths(2, t, LANE.near + 5, H);
  drawCoupleKrathong(t);
  if (own) drawFalling(1, t);
  drawForeground(t, own);
  if (own) drawFalling(2, t);
  drawWeather(ctx, wx.shared, 2, t, wxc[2], reducedMotion);
  ctx.drawImage(VIG_C, 0, 0);
}

export default {
  id: 'bangkok',
  name: 'Bangkok',
  tagline: 'Loy Krathong on the river',
  timeZone: 'Asia/Bangkok',
  accent: '#ffe08a',
  focusX: PX,
  landmark: [PX - PLAT, PX + PLAT],                    // Wat Arun: the central prang and its four satellites on their platform
  landmarkTop: TIP,                                    // the row of the central prang's trident, which landscape crops keep
  billboard: BB,
  ambience: { water: 0.45, crickets: 0.35, crowd: 0.22, wind: 0.1, traffic: 0.06 },
  events: [
    { t: BARGES.a + 1, type: 'boat', dur: 108, pan: [0.9, -0.9], gain: 0.3 },
    { t: EXPRESS.a, type: 'boat', dur: EXPRESS.b - EXPRESS.a, pan: [-1, 1], gain: 0.45 }, { t: EXPRESS.a + 0.6, type: 'horn', x: 10 },
    ...LONGTAILS.map(L => ({ t: L.a, type: 'boat', dur: L.b - L.a, pan: L.dir > 0 ? [-1, 1] : [1, -1], gain: 0.85 })),
    { t: CRUISE.a, type: 'boat', dur: CRUISE.b - CRUISE.a, pan: [1, -1], gain: 0.25 },
    ...[0, 1, 2, 3].map(k => ({ t: k * KRATHONG_T + 7, type: 'splash', x: 164, gain: 0.25 })),
    ...CHIMES.map(t => ({ t, type: 'chime', x: SH.x, gain: 0.5 })),
    { t: 100, type: 'bell', x: PX, gain: 0.4 }, { t: 220, type: 'bell', x: PX, gain: 0.4 },
  ].sort((a, b) => a.t - b.t),
  create(c) {
    ctx = c;
    ctx.imageSmoothingEnabled = false;
    prerender();
    return { render: renderFrame };
  },
};
